/**
 * AddSpecimenModal — Full CryoVault specimen entry
 *
 * EMBER-styled CryoVault entry surface with:
 * - OCR document upload (optional first step)
 * - Multi-cycle support (eggs + embryos in one session)
 * - Individual embryo entry mode with grades, genetics, sex
 * - File attachments
 * - Save logic via useCreateSpecimen
 */

import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import {
  Pressable,
  ScrollView,
  View,
  TextInput,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import * as Localization from "expo-localization";
import { MoonText as Text } from "@louise/moon-ui/primitives/MoonText";
import {
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
  MoonStepper,
  MoonSearchInput,
  SoftSecondaryButton,
  IconButton,
} from "@louise/moon-ui";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";
import {
  X,
  Plus,
  AlertTriangle,
  ChevronLeft,
  ChevronDown,
  Trash2,
  FileText,
  FileDigit,
  FolderPlus,
  Egg,
  Droplets,
  FlaskConical,
  CalendarDays,
  CircleHelp,
  Clock3,
  ClipboardList,
  Dna,
  HeartHandshake,
  MapPin,
  Snowflake,
  UserRound,
  UsersRound,
  Activity,
  type LucideIcon,
} from "lucide-react-native";
import * as DocumentPicker from "expo-document-picker";
import {
  projectDocumentProcessingRunToPrefill,
  useDocumentSourceTransfer,
} from "@src/packages/documents/ingestion";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  SpecimenBatchError,
  type SpecimenBatchResult,
  useCreateSpecimen,
  useSpecimens,
} from "@src/packages/care/cryovault/useSpecimens";
import {
  resolveSpecimenCycleId,
  shouldCreateSpecimenCycle,
  UNLINKED_SPECIMEN_CYCLE_ID,
} from "@src/packages/care/cryovault/specimenCycleSelection";
import { isValidSpecimenCycle } from "@src/packages/care/cryovault/specimenValidation";
import { getUnfiledSpecimenAttachmentNames } from "@src/packages/care/cryovault/specimenAttachmentRecovery";
import {
  CYCLE_TYPE_LABEL_KEYS,
  useCycles,
  type CycleType,
} from "@src/packages/cycles/hooks/useCycles";
import { useCanonicalCycleIntake } from "@src/packages/care/clinical-record";
import {
  type Cycle,
  type OocyteCohortDisposition,
  type SpecimenFormType,
  type SpecimenSource,
  type StorageState,
  type EmbryoIndividualEntry,
  type DropdownOption,
  type FileAttachment,
  createEmptyCycle,
  createEmptyEmbryoEntry,
  COUNTRIES,
  STORAGE_LOCATION_TYPES,
  FREEZING_METHODS,
  EMBRYO_DAY_OPTIONS,
  EMBRYO_GRADES,
  FERTILISATION_METHODS,
  PGT_OPTIONS,
  GENETIC_STATUSES,
  EMBRYO_STATUSES,
  SEX_OPTIONS,
  SEMEN_PROCESSING_METHODS,
  SEMEN_COLLECTION_METHODS,
} from "@src/packages/care/components/frozen-storage-data";
import { localClinicNames } from "@src/packages/clinics/localClinicsData";
import {
  extractPlaceArea,
  getPlaceDetails,
} from "@src/packages/clinics/utils/googlePlaces";
import { usePlacesPredictions } from "@src/packages/hooks/usePlacesPredictions";
import { EMBER } from "../../../../onboarding/theme";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../../components/FactSheetTabControl";
import PillSelect from "../../../components/PillSelect";
import {
  colors as MOON_COLORS,
  semanticColors,
} from "@louise/moon-ui/theme/tokens";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
// Pulled from the curated clinics dataset (US + FR + CA + MX). Replaces the
// 21-clinic `CLINIC_OPTIONS` list that previously seeded this picker — the
// dataset now powers the same search experience the rest of the app uses.
const CLINIC_OPTIONS: readonly string[] = localClinicNames;

// ── Shared styles ──

const BORDER = semanticColors.border.subtle;
const TEXT_FAINT = EMBER.colors.textFaint;
const TEXT_MID = EMBER.colors.textMid;
const WARM = EMBER.colors.warm;
const CRYO_ACCENT = EMBER.colors.cryo;
const EMBRYO_ACCENT = EMBER.colors.embryo;
const SELECTION_ACCENT = MOON_COLORS.deepBrown;
const CRYO_VAULT_ILLUSTRATION =
  require("@src/apps/moon/assets/images/cryo-vault-fridge.png") as number;
const SEMEN_SAMPLE_ILLUSTRATION =
  require("../../../../assets/images/3d/semen-sample.png") as number;
const OOCYTE_SAMPLE_ILLUSTRATION =
  require("../../../../assets/images/3d/oocyte-sample.png") as number;
const EMBRYO_SAMPLE_ILLUSTRATION =
  require("../../../../assets/images/embryo-microscope.jpg") as number;

const SPECIMEN_ILLUSTRATIONS: Record<
  SpecimenFormType,
  { source: number; width: number; height: number }
> = {
  eggs: { source: OOCYTE_SAMPLE_ILLUSTRATION, width: 104, height: 104 },
  sperm: { source: SEMEN_SAMPLE_ILLUSTRATION, width: 172, height: 78 },
  embryos: { source: EMBRYO_SAMPLE_ILLUSTRATION, width: 108, height: 82 },
};

const FREEZING_METHOD_ICONS: Record<string, LucideIcon> = {
  vitrification: Snowflake,
  slow_freeze: Clock3,
  unknown: CircleHelp,
};

const fieldBorder = {
  borderRadius: 12,
  borderWidth: 1,
  borderColor: BORDER,
  backgroundColor: "rgba(255,255,255,0.5)",
};

function normalizeCountryCode(value: string | null | undefined): string | null {
  const normalized = value?.trim().toUpperCase();
  if (!normalized) return null;
  if (normalized === "USA") return "US";
  return COUNTRIES.some((country) => country.id === normalized)
    ? normalized
    : null;
}

function getDeviceCountryCode(): string {
  try {
    const regionCode = normalizeCountryCode(
      Localization.getLocales()[0]?.regionCode,
    );
    if (regionCode) return regionCode;
  } catch {
    // Fall back to the existing default when device region is unavailable.
  }
  return "US";
}

function shiftIsoDate(value: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return "";

  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  if (Number.isNaN(date.getTime())) return "";
  date.setUTCDate(date.getUTCDate() + days);
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function createCycleWithCountry(country: string): Cycle {
  const cycle = createEmptyCycle();
  cycle.country = country;
  return cycle;
}

function StepDots({
  current,
  total,
  label,
}: {
  current: number;
  total: number;
  label?: string;
}) {
  return (
    <View
      testID="add-specimen-step-dots"
      style={{
        alignItems: "center",
        marginBottom: 20,
      }}
      accessibilityLabel={`Step ${current + 1} of ${total}`}
    >
      <View style={{ flexDirection: "row", gap: 6 }}>
        {Array.from({ length: total }).map((_, i) => (
          <View
            key={i}
            style={{
              width: i === current ? 18 : 6,
              height: 6,
              borderRadius: 3,
              backgroundColor:
                i === current
                  ? EMBER.colors.warm
                  : i < current
                    ? "rgba(184, 74, 22, 0.35)"
                    : "rgba(0, 0, 0, 0.06)",
            }}
          />
        ))}
      </View>
      {label && (
        <Text
          style={{
            marginTop: 8,
            color: TEXT_MID,
            fontSize: 11,
            fontWeight: "600",
          }}
        >
          {label}
        </Text>
      )}
    </View>
  );
}

// ── Props ──

export type SpecimenPrefill = {
  type?: "eggs" | "embryos" | "sperm";
  clinicName?: string;
  retrievalDate?: string;
  freezeDate?: string;
  collectedAt?: string;
  totalRetrieved?: number;
  matureMII?: number;
  numberFrozen?: number;
  numberFertilized?: number;
  cohortDisposition?: OocyteCohortDisposition;
  fertilizationMethod?: string;
  embryoGrade?: string;
  embryoDayOfDevelopment?: string;
  numberTested?: number;
  numberEuploid?: number;
  numberAneuploid?: number;
  numberMosaic?: number;
  numberNoResult?: number;
  storageLocation?: string;
  // Sperm-specific prefill (from the treatment hub's sperm_collection +
  // analysis steps).
  abstinencePeriod?: number;
  concentration?: number;
  totalCount?: number;
  motilityPercent?: number;
  morphologyPercent?: number;
  volumeMl?: number;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  cycleId?: string | null;
  prefill?: SpecimenPrefill;
  /** Called after the specimen save succeeds, with the saved form values. */
  onSaved?: (prefill: SpecimenPrefill) => void | Promise<void>;
  initialFile?: {
    name: string;
    uri: string;
    mimeType?: string | null;
  } | null;
};

type ModalStep = "upload" | "form";
type FormStep =
  | "related"
  | "analysis"
  | "counts"
  | "usage"
  | "development"
  | "inventory"
  | "details";
type SaveState = "idle" | "saving" | "saved" | "error";
type RelatedCycleReference = {
  id: string;
  cycleType: CycleType;
  cycleNumber?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  status?: string | null;
  /** Optional location metadata supplied by richer cycle fixtures/APIs. */
  country?: string | null;
  clinicName?: string | null;
  provider?: string | null;
  phases?: {
    type: string;
    startDate?: string | null;
    endDate?: string | null;
  }[];
  /** Deterministic review-fixture fallback when live specimen data is absent. */
  existingSpecimenCount?: number;
  isDraft?: boolean;
};

const RELEVANT_CYCLE_TYPES: Record<SpecimenFormType, readonly CycleType[]> = {
  eggs: ["ivf", "icsi", "egg_freezing", "embryo_freezing"],
  sperm: ["ivf", "icsi", "iui", "sperm_freezing", "embryo_freezing"],
  embryos: ["ivf", "icsi", "fet", "embryo_freezing"],
};

function cycleTypeForSpecimenType(type: SpecimenFormType): CycleType {
  if (type === "eggs") return "egg_freezing";
  if (type === "embryos") return "embryo_freezing";
  return "sperm_freezing";
}

function specimenCycleDate(cycle: Cycle): string | null {
  if (cycle.type === "eggs") {
    return cycle.oocyte.retrievalDate || cycle.oocyte.freezeDate || null;
  }
  if (cycle.type === "sperm") {
    return cycle.sperm.collectedAt || cycle.sperm.freezeDate || null;
  }
  return cycle.embryo.fertilisationDate || cycle.embryo.freezeDate || null;
}

const DEV_RELATED_CYCLES: RelatedCycleReference[] = [
  {
    id: "dev-cycle-ivf-2026-03",
    cycleType: "ivf",
    cycleNumber: 1,
    startDate: "2026-03-01",
    endDate: "2026-03-18",
    status: "closed",
    clinicName: "Spring Fertility — East Bay",
    country: "US",
    phases: [
      {
        type: "fertilization",
        startDate: "2026-03-09",
        endDate: "2026-03-09",
      },
      {
        type: "embryo_development",
        startDate: "2026-03-10",
        endDate: "2026-03-15",
      },
    ],
    existingSpecimenCount: 10,
  },
  {
    id: "dev-cycle-egg-freezing-2024-09",
    cycleType: "egg_freezing",
    cycleNumber: 1,
    startDate: "2024-09-01",
    status: "closed",
    clinicName: "Extend Fertility",
    country: "US",
    existingSpecimenCount: 7,
  },
  {
    id: "dev-cycle-fet-2026-07",
    cycleType: "fet",
    cycleNumber: 1,
    startDate: "2026-07-01",
    status: "open",
    clinicName: "Spring Fertility — East Bay",
    country: "US",
  },
];

function getDevRelatedCycles(): RelatedCycleReference[] {
  if (!__DEV__ || typeof window === "undefined") return [];
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("dev_cryo_vault_scenario") === "specimen_modal"
      ? DEV_RELATED_CYCLES
      : [];
  } catch {
    return [];
  }
}

function getRelatedCyclePhaseDate(
  cycle: RelatedCycleReference,
  phaseType: string,
  prefer: "start" | "end",
): string | null {
  const phase = cycle.phases?.find((candidate) => candidate.type === phaseType);
  if (!phase) return null;
  return prefer === "start"
    ? (phase.startDate ?? phase.endDate ?? null)
    : (phase.endDate ?? phase.startDate ?? null);
}

// ============================================================================
// Sub-components (EMBER-styled)
// ============================================================================

function SemenAnalysisInput({
  label,
  value,
  unit,
  onChange,
  labelIcon: LabelIcon,
}: {
  label: string;
  value: number | null;
  unit?: string;
  onChange: (v: number | null) => void;
  labelIcon?: LucideIcon;
}) {
  const [focused, setFocused] = useState(false);

  const handleChange = (raw: string) => {
    const normalized = raw.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = normalized.split(".");
    const cleaned =
      parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : normalized;
    if (cleaned === "" || cleaned === ".") {
      onChange(null);
      return;
    }
    const parsed = Number.parseFloat(cleaned);
    if (!Number.isNaN(parsed)) onChange(parsed);
  };

  return (
    <MoonFormField label={label} labelIcon={LabelIcon}>
      <MoonFieldInput focused={focused} paddingY={0}>
        <View
          style={{
            flex: 1,
            minWidth: 0,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <TextInput
            value={value == null ? "" : String(value)}
            accessibilityLabel={label}
            onChangeText={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            keyboardType="decimal-pad"
            inputMode="decimal"
            placeholder="—"
            placeholderTextColor={TEXT_FAINT}
            selectionColor={SELECTION_ACCENT}
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 44,
              paddingHorizontal: 0,
              paddingVertical: 12,
              color: EMBER.colors.text,
              fontSize: 14,
              lineHeight: 20,
            }}
          />
          {unit && (
            <Text
              style={{
                color: TEXT_MID,
                fontSize: 14,
                marginLeft: 10,
                flexShrink: 0,
              }}
            >
              {unit}
            </Text>
          )}
        </View>
      </MoonFieldInput>
    </MoonFormField>
  );
}

function OptionalNumber({
  label,
  value,
  onChange,
  accentColor,
  labelIcon: LabelIcon,
  editable = false,
  alwaysShowStepper = false,
  accessibilityLabel,
  showUnknownToggle = false,
  unknownLabel,
  unknownSelected = false,
  onUnknownChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  accentColor: string;
  labelIcon?: LucideIcon;
  editable?: boolean;
  alwaysShowStepper?: boolean;
  accessibilityLabel?: string;
  showUnknownToggle?: boolean;
  unknownLabel?: string;
  unknownSelected?: boolean;
  onUnknownChange?: (selected: boolean) => void;
}) {
  const capturePress = usePressCapture("OptionalNumber");
  const t = useTranslations();
  const resolvedUnknownLabel =
    unknownLabel ?? t("cryo_vault.add_modal.oocyte.i_dont_know");

  if (showUnknownToggle) {
    return (
      <View style={{ gap: 6 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            flexShrink: 1,
          }}
        >
          {LabelIcon && (
            <LabelIcon
              size={13}
              color={MOON_COLORS.textLight}
              strokeWidth={1.75}
            />
          )}
          <Text style={{ fontSize: 13, color: MOON_COLORS.textLight }}>
            {label}
          </Text>
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
          }}
        >
          <MoonStepper
            value={value ?? 0}
            onChange={(next) => {
              onUnknownChange?.(false);
              onChange(next);
            }}
            min={0}
            editable
            displayValue={value == null ? "—" : undefined}
            accessibilityLabel={accessibilityLabel ?? label}
            accentColor={accentColor}
          />
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(resolvedUnknownLabel),
                "toggle_unknown_count",
              );
              const nextSelected = !unknownSelected;
              onUnknownChange?.(nextSelected);
              if (nextSelected && !onUnknownChange) onChange(null);
            }}
            accessibilityRole="button"
            accessibilityLabel={resolvedUnknownLabel}
            accessibilityState={{ selected: unknownSelected }}
            aria-selected={unknownSelected}
            style={{
              paddingHorizontal: 10,
              paddingVertical: 7,
              borderRadius: 8,
              borderWidth: 1,
              borderColor: unknownSelected
                ? `${SELECTION_ACCENT}30`
                : "rgba(0,0,0,0.06)",
              backgroundColor: unknownSelected
                ? `${SELECTION_ACCENT}18`
                : "transparent",
            }}
          >
            <Text
              style={{
                fontSize: 12,
                fontWeight: unknownSelected ? "600" : "400",
                color: unknownSelected ? SELECTION_ACCENT : TEXT_MID,
              }}
            >
              {resolvedUnknownLabel}
            </Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (value == null && !alwaysShowStepper) {
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 6,
            flexShrink: 1,
          }}
        >
          {LabelIcon && (
            <LabelIcon
              size={13}
              color={MOON_COLORS.textLight}
              strokeWidth={1.75}
            />
          )}
          <Text style={{ fontSize: 13, color: MOON_COLORS.textLight }}>
            {label}
          </Text>
        </View>
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(t("cryo_vault.add_modal.optional_add_button")),
              "cryo_vault.add_modal.optional_add_button",
            );
            onChange(0);
          }}
          style={{
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 6,
            backgroundColor: `${accentColor}08`,
          }}
        >
          <Text style={{ fontSize: 11, color: accentColor, fontWeight: "500" }}>
            {t("cryo_vault.add_modal.optional_add_button")}
          </Text>
        </Pressable>
      </View>
    );
  }
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          flexShrink: 1,
        }}
      >
        {LabelIcon && (
          <LabelIcon
            size={13}
            color={MOON_COLORS.textLight}
            strokeWidth={1.75}
          />
        )}
        <Text style={{ fontSize: 13, color: MOON_COLORS.textLight }}>
          {label}
        </Text>
      </View>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
        <MoonStepper
          value={value ?? 0}
          onChange={(v) => onChange(v)}
          accentColor={accentColor}
          editable={editable}
          displayValue={value == null ? "—" : undefined}
          accessibilityLabel={accessibilityLabel ?? label}
        />
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel("clear_optional_count"),
              "clear_optional_count",
            );
            onChange(null);
          }}
          style={{ padding: 4 }}
        >
          <X size={12} color={TEXT_FAINT} />
        </Pressable>
      </View>
    </View>
  );
}

function InlineDropdown({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
}) {
  const capturePress = usePressCapture("InlineDropdown");
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  const effectivePlaceholder =
    placeholder ?? t("cryo_vault.add_modal.fields.select_placeholder");

  return (
    <View>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(selected?.label ?? effectivePlaceholder),
            "open_inline_dropdown",
          );
          setIsOpen(!isOpen);
        }}
        style={{
          ...fieldBorder,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingHorizontal: 12,
          paddingVertical: 10,
        }}
      >
        <Text
          style={{
            fontSize: 13,
            color: selected ? EMBER.colors.text : TEXT_FAINT,
          }}
        >
          {selected?.label ?? effectivePlaceholder}
        </Text>
        <ChevronDown size={14} color={TEXT_FAINT} />
      </Pressable>
      {isOpen && (
        <View
          style={{
            marginTop: 4,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: BORDER,
            backgroundColor: "rgba(255,255,255,0.98)",
            overflow: "hidden",
          }}
        >
          {options.map((opt) => (
            <Pressable
              key={opt.id}
              onPress={() => {
                capturePress(pressLabel(opt.label), "inline_dropdown_option");
                onChange(opt.id);
                setIsOpen(false);
              }}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 10,
                backgroundColor: opt.id === value ? `${WARM}08` : "transparent",
              }}
            >
              <Text
                style={{
                  fontSize: 13,
                  color: opt.id === value ? EMBER.colors.text : TEXT_MID,
                  fontWeight: opt.id === value ? "500" : "400",
                }}
              >
                {opt.label}
              </Text>
            </Pressable>
          ))}
        </View>
      )}
    </View>
  );
}

function EmbryoDevelopmentGlyph({
  day,
  color,
}: {
  day: string;
  color: string;
}) {
  const layouts: Record<string, [number, number][]> = {
    "1": [[11, 8]],
    "2": [
      [8, 8],
      [14, 8],
    ],
    "3": [
      [11, 3],
      [7, 12],
      [15, 12],
    ],
    "4": [
      [7, 5],
      [15, 5],
      [7, 13],
      [15, 13],
    ],
    "5": [
      [11, 2],
      [6, 7],
      [16, 7],
      [8, 15],
      [14, 15],
    ],
    "6": [
      [7, 3],
      [15, 3],
      [5, 10],
      [11, 10],
      [17, 10],
      [11, 17],
    ],
    "7": [
      [7, 2],
      [15, 2],
      [5, 8],
      [11, 8],
      [17, 8],
      [7, 15],
      [15, 15],
    ],
  };
  const cells = layouts[day] ?? layouts["1"] ?? [];

  return (
    <View
      pointerEvents="none"
      style={{ width: 28, height: 23, position: "relative" }}
    >
      {cells.map(([left, top], index) => (
        <View
          key={`${day}-${index}`}
          style={{
            position: "absolute",
            left,
            top,
            width: 7,
            height: 7,
            borderRadius: 4,
            backgroundColor: color,
            borderWidth: 1,
            borderColor: "rgba(255,255,255,0.75)",
          }}
        />
      ))}
    </View>
  );
}

function EmbryoDayPicker({
  options,
  value,
  onChange,
  compact = false,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (id: string) => void;
  compact?: boolean;
}) {
  const capturePress = usePressCapture("EmbryoDayPicker");
  return (
    <View
      testID="cryo-embryo-day-picker"
      accessibilityRole="radiogroup"
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: compact ? 4 : 8,
      }}
    >
      {options.map((option) => {
        const selected = option.id === value;
        const [dayLabel, stageLabel] = option.label.split(" (");
        return (
          <Pressable
            key={option.id}
            testID={`cryo-embryo-day-option-${option.id}`}
            accessibilityRole="radio"
            accessibilityLabel={option.label}
            accessibilityState={{ selected }}
            onPress={() => {
              capturePress(pressLabel(option.label), "specimen_option");
              onChange(option.id);
            }}
            style={({ pressed }) => [
              {
                flexGrow: 1,
                flexBasis: compact ? "12%" : "22%",
                minWidth: compact ? 34 : 68,
                minHeight: compact ? 48 : 68,
                paddingHorizontal: compact ? 2 : 6,
                paddingVertical: compact ? 4 : 7,
                borderRadius: 12,
                alignItems: "center",
                justifyContent: "center",
                gap: compact ? 1 : 3,
                borderWidth: 1,
                borderColor: selected ? CRYO_ACCENT : BORDER,
                backgroundColor: selected
                  ? `${CRYO_ACCENT}14`
                  : "rgba(255,255,255,0.5)",
                opacity: pressed ? 0.82 : 1,
              },
            ]}
          >
            <EmbryoDevelopmentGlyph
              day={option.id}
              color={selected ? CRYO_ACCENT : TEXT_MID}
            />
            <Text
              style={{
                fontSize: compact ? 10 : 12,
                lineHeight: compact ? 12 : 15,
                fontWeight: "700",
                color: selected ? CRYO_ACCENT : EMBER.colors.text,
              }}
            >
              {dayLabel}
            </Text>
            {!compact && stageLabel && (
              <Text
                numberOfLines={1}
                style={{
                  fontSize: 9,
                  lineHeight: 11,
                  color: selected ? CRYO_ACCENT : TEXT_FAINT,
                }}
              >
                {stageLabel.replace(")", "")}
              </Text>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

function ClinicPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const capturePress = usePressCapture("ClinicPicker");
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return CLINIC_OPTIONS;
    const q = search.toLowerCase();
    return CLINIC_OPTIONS.filter((c) => c.toLowerCase().includes(q));
  }, [search]);

  return (
    <View>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              t(value || "cryo_vault.add_modal.fields.clinic_placeholder"),
            ),
            "open_clinic_picker",
          );
          setIsOpen(!isOpen);
          setSearch("");
        }}
        style={{
          ...fieldBorder,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingHorizontal: 12,
          paddingVertical: 10,
        }}
      >
        <Text
          style={{
            flex: 1,
            fontSize: 13,
            color: value ? EMBER.colors.text : TEXT_FAINT,
          }}
          numberOfLines={1}
        >
          {value || t("cryo_vault.add_modal.fields.clinic_placeholder")}
        </Text>
        <ChevronDown size={14} color={TEXT_FAINT} />
      </Pressable>

      {isOpen && (
        <View
          style={{
            marginTop: 4,
            borderRadius: 12,
            borderWidth: 1,
            borderColor: BORDER,
            backgroundColor: "rgba(255,255,255,0.98)",
            overflow: "hidden",
          }}
        >
          <View
            style={{
              borderBottomWidth: 1,
              borderColor: BORDER,
              paddingHorizontal: 8,
              paddingVertical: 6,
            }}
          >
            <MoonSearchInput
              value={search}
              onChangeText={setSearch}
              placeholder={t("cryo_vault.add_modal.fields.search_placeholder")}
              autoFocus
              onClear={() => setSearch("")}
            />
          </View>
          <ScrollView style={{ maxHeight: 180 }}>
            {filtered.map((clinic) => (
              <Pressable
                key={clinic}
                onPress={() => {
                  capturePress(pressLabel(clinic), "clinic_option");
                  onChange(clinic);
                  setIsOpen(false);
                }}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  backgroundColor:
                    clinic === value ? `${WARM}08` : "transparent",
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    color: clinic === value ? EMBER.colors.text : TEXT_MID,
                  }}
                >
                  {clinic}
                </Text>
              </Pressable>
            ))}
            {search.trim() &&
              !CLINIC_OPTIONS.some(
                (c) => c.toLowerCase() === search.trim().toLowerCase(),
              ) && (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        t("cryo_vault.add_modal.fields.use_custom", {
                          params: { value: search.trim() },
                        }),
                      ),
                      "use_custom_clinic",
                    );
                    onChange(search.trim());
                    setIsOpen(false);
                  }}
                  style={{
                    borderTopWidth: 1,
                    borderColor: BORDER,
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                  }}
                >
                  <Text style={{ fontSize: 13, color: CRYO_ACCENT }}>
                    {t("cryo_vault.add_modal.fields.use_custom", {
                      params: { value: search.trim() },
                    })}
                  </Text>
                </Pressable>
              )}
          </ScrollView>
        </View>
      )}
    </View>
  );
}

// ============================================================================
// Oocyte Fields Section
// ============================================================================

function OocyteFieldsSection({
  cycle,
  onUpdate,
  section,
}: {
  cycle: Cycle;
  onUpdate: (updates: Partial<Cycle>) => void;
  section: "counts" | "usage" | "details";
}) {
  const t = useTranslations();
  const o = cycle.oocyte;
  const accent = CRYO_ACCENT;

  const updateOocyte = (updates: Partial<Cycle["oocyte"]>) => {
    onUpdate({ oocyte: { ...o, ...updates } });
  };

  type OocyteCountMetric =
    "totalRetrieved" | "matureMII" | "immatureMI" | "degenerated";
  const [explicitUnknownMetrics, setExplicitUnknownMetrics] = useState<
    Record<OocyteCountMetric, boolean>
  >({
    totalRetrieved: false,
    matureMII: false,
    immatureMI: false,
    degenerated: false,
  });

  const updateOocyteCount = (
    metric: OocyteCountMetric,
    value: number | null,
  ) => {
    setExplicitUnknownMetrics((previous) => ({
      ...previous,
      [metric]: false,
    }));
    updateOocyte({ [metric]: value });
  };

  const setOocyteCountUnknown = (
    metric: OocyteCountMetric,
    selected: boolean,
  ) => {
    setExplicitUnknownMetrics((previous) => ({
      ...previous,
      [metric]: selected,
    }));
    if (selected) updateOocyte({ [metric]: null });
  };

  const freezingMethodOptions = useMemo(
    () =>
      FREEZING_METHODS.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.freezing_method.${opt.id}`),
        icon: FREEZING_METHOD_ICONS[opt.id],
      })),
    [t],
  );

  const dispositionOptions = useMemo(
    () => [
      {
        id: "all_frozen",
        label: t("cryo_vault.add_modal.oocyte.disposition_all_frozen"),
        icon: Snowflake,
      },
      {
        id: "mixed_frozen_fertilized",
        label: t(
          "cryo_vault.add_modal.oocyte.disposition_mixed_frozen_fertilized",
        ),
        icon: FlaskConical,
      },
      {
        id: "all_fertilized",
        label: t("cryo_vault.add_modal.oocyte.disposition_all_fertilized"),
        icon: FlaskConical,
      },
      {
        id: "unknown",
        label: t("cryo_vault.add_modal.oocyte.disposition_unknown"),
        icon: CircleHelp,
      },
    ],
    [t],
  );

  const dispositionValue = o.cohortDisposition ?? "";
  const knownCohortCount = o.matureMII ?? o.totalRetrieved;
  const suggestedFrozenCount = knownCohortCount ?? o.numberFrozen;
  const suggestedFertilizedCount = knownCohortCount ?? o.numberFertilized ?? 0;
  const [frozenCountWasEdited, setFrozenCountWasEdited] = useState(false);

  const updateFrozenCount = (value: number) => {
    setFrozenCountWasEdited(true);
    updateOocyte({
      numberFrozen: value,
      ...(o.numberThawed != null && o.numberThawed > value
        ? {
            numberThawed: value,
            currentStatus: value === 0 ? "in_storage" : "all_thawed",
          }
        : {}),
    });
  };

  const updateFertilizedCount = (value: number) => {
    const inferredFrozenCount =
      knownCohortCount == null
        ? o.numberFrozen
        : Math.max(0, knownCohortCount - value);

    updateOocyte({
      numberFertilized: value,
      ...(dispositionValue === "mixed_frozen_fertilized" &&
      !frozenCountWasEdited
        ? { numberFrozen: inferredFrozenCount }
        : {}),
    });
  };

  const handleDispositionChange = (value: string) => {
    const disposition = value as OocyteCohortDisposition;
    setFrozenCountWasEdited(false);

    if (disposition === "all_fertilized") {
      updateOocyte({
        cohortDisposition: disposition,
        numberFrozen: 0,
        numberFertilized: suggestedFertilizedCount,
        currentStatus: "used",
      });
      return;
    }

    updateOocyte({
      cohortDisposition: disposition,
      ...(disposition === "all_frozen"
        ? { numberFrozen: suggestedFrozenCount, numberFertilized: null }
        : disposition === "mixed_frozen_fertilized"
          ? {
              numberFrozen:
                knownCohortCount == null
                  ? o.numberFrozen
                  : Math.max(0, knownCohortCount - 1),
              numberFertilized: 1,
              currentStatus: "in_storage",
            }
          : { numberFertilized: null, currentStatus: "in_storage" }),
    });
  };

  return (
    <View style={{ gap: 12 }}>
      {section === "counts" && (
        <>
          {/* Retrieval details stay together because the date and total refer
              to the same clinical event. */}
          <MoonDateField
            label={t("cryo_vault.add_modal.oocyte.retrieval_date")}
            labelIcon={CalendarDays}
            value={o.retrievalDate}
            onChange={(iso) => updateOocyte({ retrievalDate: iso })}
          />
          <OptionalNumber
            label={t("cryo_vault.add_modal.oocyte.total_retrieved")}
            labelIcon={ClipboardList}
            value={o.totalRetrieved}
            onChange={(v) => updateOocyteCount("totalRetrieved", v)}
            accentColor={accent}
            editable
            showUnknownToggle
            unknownSelected={explicitUnknownMetrics.totalRetrieved}
            onUnknownChange={(selected) =>
              setOocyteCountUnknown("totalRetrieved", selected)
            }
          />

          {/* Optional lab breakdown — kept flat so patients can stop after the
          clinically important cohort totals above. */}
          <OptionalNumber
            label={t("cryo_vault.add_modal.oocyte.mature_mii")}
            labelIcon={Egg}
            value={o.matureMII}
            onChange={(v) => updateOocyteCount("matureMII", v)}
            accentColor={accent}
            editable
            showUnknownToggle
            unknownSelected={explicitUnknownMetrics.matureMII}
            onUnknownChange={(selected) =>
              setOocyteCountUnknown("matureMII", selected)
            }
          />
          <OptionalNumber
            label={t("cryo_vault.add_modal.oocyte.immature_mi")}
            labelIcon={Egg}
            value={o.immatureMI}
            onChange={(v) => updateOocyteCount("immatureMI", v)}
            accentColor={accent}
            editable
            showUnknownToggle
            unknownSelected={explicitUnknownMetrics.immatureMI}
            onUnknownChange={(selected) =>
              setOocyteCountUnknown("immatureMI", selected)
            }
          />
          <OptionalNumber
            label={t("cryo_vault.add_modal.oocyte.degenerated")}
            labelIcon={CircleHelp}
            value={o.degenerated}
            onChange={(v) => updateOocyteCount("degenerated", v)}
            accentColor={accent}
            editable
            showUnknownToggle
            unknownSelected={explicitUnknownMetrics.degenerated}
            onUnknownChange={(selected) =>
              setOocyteCountUnknown("degenerated", selected)
            }
          />
        </>
      )}

      {section === "usage" && (
        <>
          <MoonFormField
            label={t("cryo_vault.add_modal.oocyte.cohort_disposition")}
            labelIcon={Activity}
          >
            <InlineOptionPicker
              options={dispositionOptions}
              value={dispositionValue}
              onChange={handleDispositionChange}
              wrap
              columns={2}
            />
          </MoonFormField>

          {(dispositionValue === "mixed_frozen_fertilized" ||
            dispositionValue === "all_fertilized") && (
            <MoonFormField
              label={t("cryo_vault.add_modal.oocyte.number_fertilized")}
              labelIcon={FlaskConical}
              hint={t("cryo_vault.add_modal.oocyte.number_fertilized_hint")}
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingVertical: 2,
                }}
              >
                <MoonStepper
                  value={o.numberFertilized ?? suggestedFertilizedCount}
                  onChange={updateFertilizedCount}
                  min={1}
                  max={knownCohortCount ?? undefined}
                  editable
                  accessibilityLabel={t(
                    "cryo_vault.add_modal.oocyte.number_fertilized",
                  )}
                  accentColor={accent}
                />
              </View>
            </MoonFormField>
          )}

          {dispositionValue !== "all_fertilized" && dispositionValue !== "" && (
            <MoonFormField
              label={
                dispositionValue === "unknown"
                  ? t("cryo_vault.add_modal.oocyte.number_frozen_now")
                  : t("cryo_vault.add_modal.oocyte.number_frozen")
              }
              labelIcon={Snowflake}
              hint={
                knownCohortCount != null
                  ? t(
                      "cryo_vault.add_modal.oocyte.number_frozen_prefill_hint",
                      { params: { count: String(suggestedFrozenCount) } },
                    )
                  : undefined
              }
            >
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingVertical: 2,
                }}
              >
                <MoonStepper
                  value={o.numberFrozen}
                  onChange={updateFrozenCount}
                  min={dispositionValue === "unknown" ? 0 : 1}
                  editable
                  accessibilityLabel={t(
                    "cryo_vault.add_modal.oocyte.number_frozen",
                  )}
                  accentColor={accent}
                />
              </View>
            </MoonFormField>
          )}

          {dispositionValue === "all_fertilized" && (
            <Text
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: TEXT_MID,
              }}
            >
              {t("cryo_vault.add_modal.oocyte.all_fertilized_hint")}
            </Text>
          )}
        </>
      )}

      {section === "details" && (
        <>
          {o.cohortDisposition === "all_fertilized" && (
            <Text
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: TEXT_MID,
              }}
            >
              {t("cryo_vault.add_modal.oocyte.all_fertilized_save_hint")}
            </Text>
          )}

          {/* Freeze date belongs to the frozen subset, so keep it optional and
              only show it when this cohort includes frozen eggs. */}
          {o.numberFrozen > 0 && (
            <View style={{ gap: 12 }}>
              <MoonDateField
                label={t("cryo_vault.add_modal.oocyte.freeze_date")}
                labelIcon={CalendarDays}
                value={o.freezeDate}
                onChange={(iso) => updateOocyte({ freezeDate: iso })}
              />
              <Text style={{ fontSize: 11, color: TEXT_FAINT }}>
                {t("cryo_vault.add_modal.oocyte.freeze_date_optional")}
              </Text>
            </View>
          )}

          {o.numberFrozen > 0 && (
            <MoonFormField
              label={t("cryo_vault.add_modal.oocyte.freezing_method")}
              labelIcon={Snowflake}
            >
              <InlineOptionPicker
                options={freezingMethodOptions}
                value={o.freezingMethod}
                onChange={(v) => updateOocyte({ freezingMethod: v })}
              />
            </MoonFormField>
          )}
        </>
      )}
    </View>
  );
}

// ============================================================================
// Semen (Sperm) Fields Section
// ============================================================================

function SemenFieldsSection({
  cycle,
  onUpdate,
  section,
}: {
  cycle: Cycle;
  onUpdate: (updates: Partial<Cycle>) => void;
  section: "analysis" | "counts";
}) {
  const t = useTranslations();
  const s = cycle.sperm;
  const accent = SELECTION_ACCENT;

  const updateSperm = (updates: Partial<Cycle["sperm"]>) => {
    onUpdate({ sperm: { ...s, ...updates } });
  };

  const collectionMethodOptions = useMemo(
    () =>
      SEMEN_COLLECTION_METHODS.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.semen_collection.${opt.id}`),
        icon:
          opt.id === "ejaculation"
            ? Droplets
            : opt.id === "other"
              ? CircleHelp
              : Activity,
      })),
    [t],
  );
  const processingMethodOptions = useMemo(
    () =>
      SEMEN_PROCESSING_METHODS.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.semen_processing.${opt.id}`),
        icon: opt.id === "other" ? CircleHelp : FlaskConical,
      })),
    [t],
  );

  if (section === "analysis") {
    return (
      <View style={{ gap: 12 }}>
        <OptionalNumber
          label={t("cryo_vault.add_modal.sperm.days_abstinence")}
          labelIcon={Clock3}
          value={s.abstinencePeriod}
          onChange={(v) => updateSperm({ abstinencePeriod: v })}
          accentColor={accent}
          editable
          alwaysShowStepper
        />
        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.concentration")}
              labelIcon={Activity}
              value={s.concentration}
              unit="M/mL"
              onChange={(v) => updateSperm({ concentration: v })}
            />
          </View>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.total_count")}
              labelIcon={ClipboardList}
              value={s.totalCount}
              unit="M"
              onChange={(v) => updateSperm({ totalCount: v })}
            />
          </View>
        </View>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.motility")}
              labelIcon={Activity}
              value={s.motilityPercent}
              unit="%"
              onChange={(v) => updateSperm({ motilityPercent: v })}
            />
          </View>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.progressive_motility")}
              labelIcon={Activity}
              value={s.progressiveMotilityPercent}
              unit="%"
              onChange={(v) => updateSperm({ progressiveMotilityPercent: v })}
            />
          </View>
        </View>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.morphology")}
              labelIcon={Dna}
              value={s.morphologyPercent}
              unit="%"
              onChange={(v) => updateSperm({ morphologyPercent: v })}
            />
          </View>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.vitality")}
              labelIcon={Activity}
              value={s.vitalityPercent}
              unit="%"
              onChange={(v) => updateSperm({ vitalityPercent: v })}
            />
          </View>
        </View>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.volume")}
              labelIcon={FlaskConical}
              value={s.volumeMl}
              unit="mL"
              onChange={(v) => updateSperm({ volumeMl: v })}
            />
          </View>
          <View style={{ flex: 1 }}>
            <SemenAnalysisInput
              label={t("cryo_vault.add_modal.sperm.dna_fragmentation")}
              labelIcon={Dna}
              value={s.dnaFragmentationPercent}
              unit="%"
              onChange={(v) => updateSperm({ dnaFragmentationPercent: v })}
            />
          </View>
        </View>
        <View style={{ flexDirection: "row", gap: 10 }}>
          <View style={{ flex: 1 }}>
            <MoonFormField
              label={t("cryo_vault.add_modal.sperm.other")}
              labelIcon={FileText}
            >
              <TextInput
                value={s.otherDetails}
                onChangeText={(value) => updateSperm({ otherDetails: value })}
                placeholder={t("cryo_vault.add_modal.sperm.other_placeholder")}
                placeholderTextColor={TEXT_FAINT}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                style={{
                  ...fieldBorder,
                  minHeight: 68,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  color: EMBER.colors.text,
                  fontSize: 13,
                }}
              />
            </MoonFormField>
          </View>
        </View>
      </View>
    );
  }

  if (section === "counts") {
    return (
      <View style={{ gap: 14 }}>
        <MoonDateField
          label={t("cryo_vault.add_modal.sperm.collection_date")}
          labelIcon={CalendarDays}
          value={s.collectedAt}
          onChange={(iso) => updateSperm({ collectedAt: iso })}
        />
        <MoonFormField
          label={t("cryo_vault.add_modal.sperm.vials_frozen")}
          labelIcon={Droplets}
        >
          <View style={{ alignItems: "flex-start", paddingVertical: 2 }}>
            <MoonStepper
              value={s.numberFrozen}
              onChange={(v) => updateSperm({ numberFrozen: v })}
              min={1}
              editable
              accessibilityLabel={t("cryo_vault.add_modal.sperm.vials_frozen")}
              accentColor={accent}
            />
          </View>
        </MoonFormField>

        <MoonDateField
          label={t("cryo_vault.add_modal.sperm.freeze_date")}
          labelIcon={CalendarDays}
          value={s.freezeDate}
          onChange={(iso) => updateSperm({ freezeDate: iso })}
        />
        <Text style={{ fontSize: 11, color: TEXT_FAINT, marginTop: -6 }}>
          {t("cryo_vault.add_modal.sperm.freeze_date_optional")}
        </Text>

        <MoonFormField
          label={t("cryo_vault.add_modal.sperm.collection_method")}
          labelIcon={Droplets}
        >
          <InlineOptionPicker
            options={collectionMethodOptions}
            value={s.collectionMethod}
            onChange={(v) => updateSperm({ collectionMethod: v })}
            wrap
            columns={2}
          />
        </MoonFormField>

        <MoonFormField
          label={t("cryo_vault.add_modal.sperm.processing_method")}
          labelIcon={FlaskConical}
        >
          <InlineOptionPicker
            options={processingMethodOptions}
            value={s.processingMethod}
            onChange={(v) => updateSperm({ processingMethod: v })}
            wrap
            columns={2}
          />
        </MoonFormField>
      </View>
    );
  }
}

// ============================================================================
// Embryo Fields Section
// ============================================================================

function EmbryoEntryModeTabs({
  cycle,
  onChange,
}: {
  cycle: Cycle;
  onChange: (mode: Cycle["embryoEntryMode"]) => void;
}) {
  const t = useTranslations();
  const tabs = useMemo<FactSheetTab<Cycle["embryoEntryMode"]>[]>(
    () => [
      {
        value: "group",
        label: t("cryo_vault.add_modal.embryo.group_mode"),
        icon: ClipboardList,
      },
      {
        value: "individual",
        label: t("cryo_vault.add_modal.embryo.individual_mode"),
        shortLabel:
          cycle.embryo.individualEntries.length > 0
            ? `${t("cryo_vault.add_modal.embryo.individual_mode")} (${cycle.embryo.individualEntries.length})`
            : undefined,
        icon: FileDigit,
      },
    ],
    [cycle.embryo.individualEntries.length, t],
  );

  return (
    <FactSheetTabControl
      tabs={tabs}
      active={cycle.embryoEntryMode}
      onChange={onChange}
    />
  );
}

function EmbryoFieldsSection({
  cycle,
  onUpdate,
  section,
}: {
  cycle: Cycle;
  onUpdate: (updates: Partial<Cycle>) => void;
  section: "development" | "inventory";
}) {
  const capturePress = usePressCapture("EmbryoFieldsSection");
  const t = useTranslations();
  const e = cycle.embryo;
  const accent = EMBRYO_ACCENT;

  const embryoDayOptions = useMemo(
    () =>
      EMBRYO_DAY_OPTIONS.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.embryo_day.${opt.id}`),
      })),
    [t],
  );
  const fertilisationMethodOptions = useMemo(
    () =>
      FERTILISATION_METHODS.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.fertilisation_method.${opt.id}`),
      })),
    [t],
  );
  const pgtOptions = useMemo(
    () =>
      PGT_OPTIONS.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.pgt.${opt.id}`),
      })),
    [t],
  );
  const embryoGradeOptions = useMemo(
    () =>
      EMBRYO_GRADES.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.embryo_grade.${opt.id}`),
      })),
    [t],
  );
  const geneticStatusOptions = useMemo(
    () =>
      GENETIC_STATUSES.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.genetic_status.${opt.id}`),
      })),
    [t],
  );
  const sexOptions = useMemo(
    () =>
      SEX_OPTIONS.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.sex.${opt.id}`),
      })),
    [t],
  );
  const embryoStatusOptions = useMemo(
    () =>
      // Disposition and transfer are separate authorized lifecycle events;
      // this intake flow may only record development/storage outcomes.
      EMBRYO_STATUSES.filter((opt) =>
        ["frozen", "arrested", "thawed_refrozen"].includes(opt.id),
      ).map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.embryo_status.${opt.id}`),
      })),
    [t],
  );

  const updateEmbryo = (updates: Partial<Cycle["embryo"]>) => {
    onUpdate({ embryo: { ...e, ...updates } });
  };

  const autoStorage =
    e.numberFrozen == null
      ? null
      : e.numberFrozen - (e.numberTransferred ?? 0) - (e.numberDiscarded ?? 0);

  const developmentScope =
    cycle.embryoEntryMode === "individual" ? "individual" : "group";
  const inferredDevelopmentOffset = Number.parseInt(e.dayOfDevelopment, 10);

  const updateDevelopmentDates = (
    field: "fertilisationDate" | "freezeDate",
    value: string,
  ) => {
    const updates: Partial<Cycle["embryo"]> = { [field]: value };
    if (
      developmentScope === "group" &&
      Number.isFinite(inferredDevelopmentOffset) &&
      value
    ) {
      if (field === "fertilisationDate" && !e.freezeDate) {
        updates.freezeDate = shiftIsoDate(value, inferredDevelopmentOffset);
      } else if (field === "freezeDate" && !e.fertilisationDate) {
        updates.fertilisationDate = shiftIsoDate(
          value,
          -inferredDevelopmentOffset,
        );
      }
    }
    updateEmbryo(updates);
  };

  const updateDevelopmentDay = (value: string) => {
    const updates: Partial<Cycle["embryo"]> = { dayOfDevelopment: value };
    const dayOffset = Number.parseInt(value, 10);
    if (developmentScope === "group" && Number.isFinite(dayOffset)) {
      if (e.fertilisationDate && !e.freezeDate) {
        updates.freezeDate = shiftIsoDate(e.fertilisationDate, dayOffset);
      } else if (e.freezeDate && !e.fertilisationDate) {
        updates.fertilisationDate = shiftIsoDate(e.freezeDate, -dayOffset);
      }
    }
    updateEmbryo(updates);
  };

  type EmbryoCountMetric =
    | "totalEmbryosCreated"
    | "numberTested"
    | "numberEuploid"
    | "numberAneuploid"
    | "numberMosaic"
    | "numberNoResult"
    | "numberTransferred"
    | "numberDiscarded";
  const [explicitUnknownMetrics, setExplicitUnknownMetrics] = useState<
    Record<EmbryoCountMetric, boolean>
  >({
    totalEmbryosCreated: false,
    numberTested: false,
    numberEuploid: false,
    numberAneuploid: false,
    numberMosaic: false,
    numberNoResult: false,
    numberTransferred: false,
    numberDiscarded: false,
  });

  const updateEmbryoCount = (
    metric: EmbryoCountMetric,
    value: number | null,
  ) => {
    setExplicitUnknownMetrics((previous) => ({
      ...previous,
      [metric]: false,
    }));
    updateEmbryo({ [metric]: value });
  };

  const setEmbryoCountUnknown = (
    metric: EmbryoCountMetric,
    selected: boolean,
  ) => {
    setExplicitUnknownMetrics((previous) => ({
      ...previous,
      [metric]: selected,
    }));
    if (selected) updateEmbryo({ [metric]: null });
  };

  useEffect(() => {
    if (!e.storageOverride && cycle.embryoEntryMode === "group") {
      updateEmbryo({
        currentlyInStorage:
          autoStorage == null ? null : Math.max(0, autoStorage),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    e.numberFrozen,
    e.numberTransferred,
    e.numberDiscarded,
    e.storageOverride,
    cycle.embryoEntryMode,
  ]);

  const addIndividualEntry = () => {
    syncIndividualEntries([...e.individualEntries, createEmptyEmbryoEntry()]);
  };

  const syncIndividualEntries = (entries: EmbryoIndividualEntry[]) => {
    const frozen = entries.filter(
      (entry) =>
        entry.status === "frozen" || entry.status === "thawed_refrozen",
    ).length;
    const transferred = entries.filter(
      (entry) => entry.status === "transferred",
    ).length;
    const discarded = entries.filter(
      (entry) => entry.status === "discarded",
    ).length;
    const currentlyInStorage = entries.filter(
      (entry) =>
        entry.status === "frozen" || entry.status === "thawed_refrozen",
    ).length;
    const geneticEntries = entries.filter((entry) => entry.geneticStatus);
    const hasGeneticData = geneticEntries.length > 0;
    const geneticCounts = hasGeneticData
      ? {
          numberTested: geneticEntries.filter(
            (entry) => entry.geneticStatus !== "not_tested",
          ).length,
          numberEuploid: geneticEntries.filter(
            (entry) => entry.geneticStatus === "euploid",
          ).length,
          numberAneuploid: geneticEntries.filter(
            (entry) => entry.geneticStatus === "aneuploid",
          ).length,
          numberMosaic: geneticEntries.filter(
            (entry) =>
              entry.geneticStatus === "mosaic_low" ||
              entry.geneticStatus === "mosaic_high",
          ).length,
          numberNoResult: geneticEntries.filter(
            (entry) =>
              entry.geneticStatus === "no_result" ||
              entry.geneticStatus === "pending",
          ).length,
        }
      : {};

    if (hasGeneticData) {
      setExplicitUnknownMetrics((previous) => ({
        ...previous,
        numberTested: false,
        numberEuploid: false,
        numberAneuploid: false,
        numberMosaic: false,
        numberNoResult: false,
      }));
    }

    updateEmbryo({
      individualEntries: entries,
      numberFrozen: frozen,
      ...geneticCounts,
      numberTransferred: transferred || null,
      numberDiscarded: discarded || null,
      ...(e.totalEmbryosCreated == null
        ? { totalEmbryosCreated: entries.length }
        : {}),
      ...(e.storageOverride ? {} : { currentlyInStorage }),
    });
  };

  const updateIndividualEntry = (
    entryId: string,
    updates: Partial<EmbryoIndividualEntry>,
  ) => {
    syncIndividualEntries(
      e.individualEntries.map((entry) =>
        entry.id === entryId ? { ...entry, ...updates } : entry,
      ),
    );
  };

  const removeIndividualEntry = (entryId: string) => {
    syncIndividualEntries(
      e.individualEntries.filter((entry) => entry.id !== entryId),
    );
  };

  return (
    <View style={{ gap: 12 }}>
      {section === "development" && (
        <>
          {developmentScope === "group" && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 10,
                padding: 12,
                borderRadius: 12,
                backgroundColor: `${accent}08`,
                borderWidth: 1,
                borderColor: `${accent}20`,
              }}
            >
              <View
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${accent}14`,
                }}
              >
                <ClipboardList size={17} color={accent} />
              </View>
              <View style={{ flex: 1, gap: 3 }}>
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                  }}
                >
                  {t("cryo_vault.add_modal.embryo.development_scope_group")}
                </Text>
                <Text style={{ fontSize: 12, lineHeight: 17, color: TEXT_MID }}>
                  {t(
                    "cryo_vault.add_modal.embryo.development_scope_group_hint",
                  )}
                </Text>
              </View>
            </View>
          )}

          {/* Dates */}
          <View style={{ flexDirection: "row", gap: 10 }}>
            <View style={{ flex: 1 }}>
              <MoonDateField
                label={t("cryo_vault.add_modal.embryo.fertilisation_date")}
                labelIcon={CalendarDays}
                value={e.fertilisationDate}
                onChange={(iso) =>
                  updateDevelopmentDates("fertilisationDate", iso)
                }
              />
            </View>
            <View style={{ flex: 1 }}>
              <MoonDateField
                label={t("cryo_vault.add_modal.embryo.freeze_date")}
                labelIcon={CalendarDays}
                value={e.freezeDate}
                onChange={(iso) => updateDevelopmentDates("freezeDate", iso)}
              />
            </View>
          </View>
          {developmentScope === "group" && (
            <Text style={{ fontSize: 11, lineHeight: 16, color: TEXT_FAINT }}>
              {t("cryo_vault.add_modal.embryo.date_relationship_hint")}
            </Text>
          )}

          {/* Group mode has one shared developmental day. Individual mode
              records each embryo's furthest day in the previous tab. */}
          {developmentScope === "group" && (
            <MoonFormField
              label={t("cryo_vault.add_modal.embryo.day_of_development")}
              labelIcon={Clock3}
            >
              <EmbryoDayPicker
                options={embryoDayOptions}
                value={e.dayOfDevelopment}
                onChange={updateDevelopmentDay}
              />
            </MoonFormField>
          )}

          {/* Fertilisation method */}
          <MoonFormField
            label={t("cryo_vault.add_modal.embryo.fertilisation_method")}
            labelIcon={FlaskConical}
          >
            <InlineOptionPicker
              options={fertilisationMethodOptions}
              value={e.fertilisationMethod}
              onChange={(v) => updateEmbryo({ fertilisationMethod: v })}
              wrap
              columns={2}
            />
          </MoonFormField>

          {/* PGT */}
          <MoonFormField
            label={t("cryo_vault.add_modal.embryo.genetic_testing")}
            labelIcon={Dna}
          >
            <InlineDropdown
              options={pgtOptions}
              value={e.pgtPerformed}
              onChange={(v) => updateEmbryo({ pgtPerformed: v })}
            />
          </MoonFormField>
        </>
      )}

      {section === "inventory" && (
        <>
          {cycle.embryoEntryMode === "group" ? (
            <View style={{ gap: 12 }}>
              <OptionalNumber
                label={t("cryo_vault.add_modal.embryo.total_embryos_created")}
                labelIcon={ClipboardList}
                value={e.totalEmbryosCreated}
                onChange={(v) => updateEmbryoCount("totalEmbryosCreated", v)}
                accentColor={accent}
                editable
                showUnknownToggle
                unknownSelected={explicitUnknownMetrics.totalEmbryosCreated}
                onUnknownChange={(selected) =>
                  setEmbryoCountUnknown("totalEmbryosCreated", selected)
                }
              />

              <View style={{ gap: 12 }}>
                <OptionalNumber
                  label={t("cryo_vault.add_modal.embryo.number_tested")}
                  labelIcon={Dna}
                  value={e.numberTested}
                  onChange={(v) => updateEmbryoCount("numberTested", v)}
                  accentColor={accent}
                  editable
                  showUnknownToggle
                  unknownSelected={explicitUnknownMetrics.numberTested}
                  onUnknownChange={(selected) =>
                    setEmbryoCountUnknown("numberTested", selected)
                  }
                />
                <OptionalNumber
                  label={t("cryo_vault.add_modal.embryo.number_euploid")}
                  labelIcon={HeartHandshake}
                  value={e.numberEuploid}
                  onChange={(v) => updateEmbryoCount("numberEuploid", v)}
                  accentColor={accent}
                  editable
                  showUnknownToggle
                  unknownSelected={explicitUnknownMetrics.numberEuploid}
                  onUnknownChange={(selected) =>
                    setEmbryoCountUnknown("numberEuploid", selected)
                  }
                />
                <OptionalNumber
                  label={t("cryo_vault.add_modal.embryo.number_aneuploid")}
                  labelIcon={AlertTriangle}
                  value={e.numberAneuploid}
                  onChange={(v) => updateEmbryoCount("numberAneuploid", v)}
                  accentColor={accent}
                  editable
                  showUnknownToggle
                  unknownSelected={explicitUnknownMetrics.numberAneuploid}
                  onUnknownChange={(selected) =>
                    setEmbryoCountUnknown("numberAneuploid", selected)
                  }
                />
                <OptionalNumber
                  label={t("cryo_vault.add_modal.embryo.number_mosaic")}
                  labelIcon={Activity}
                  value={e.numberMosaic}
                  onChange={(v) => updateEmbryoCount("numberMosaic", v)}
                  accentColor={accent}
                  editable
                  showUnknownToggle
                  unknownSelected={explicitUnknownMetrics.numberMosaic}
                  onUnknownChange={(selected) =>
                    setEmbryoCountUnknown("numberMosaic", selected)
                  }
                />
                <OptionalNumber
                  label={t("cryo_vault.add_modal.embryo.number_no_result")}
                  labelIcon={CircleHelp}
                  value={e.numberNoResult}
                  onChange={(v) => updateEmbryoCount("numberNoResult", v)}
                  accentColor={accent}
                  editable
                  showUnknownToggle
                  unknownSelected={explicitUnknownMetrics.numberNoResult}
                  onUnknownChange={(selected) =>
                    setEmbryoCountUnknown("numberNoResult", selected)
                  }
                />
              </View>

              <OptionalNumber
                label={t("cryo_vault.add_modal.embryo.transferred")}
                labelIcon={Activity}
                value={e.numberTransferred}
                onChange={(v) => updateEmbryoCount("numberTransferred", v)}
                accentColor={accent}
                editable
                showUnknownToggle
                unknownSelected={explicitUnknownMetrics.numberTransferred}
                onUnknownChange={(selected) =>
                  setEmbryoCountUnknown("numberTransferred", selected)
                }
              />
              <OptionalNumber
                label={t("cryo_vault.add_modal.embryo.discarded")}
                labelIcon={Trash2}
                value={e.numberDiscarded}
                onChange={(v) => updateEmbryoCount("numberDiscarded", v)}
                accentColor={accent}
                editable
                showUnknownToggle
                unknownSelected={explicitUnknownMetrics.numberDiscarded}
                onUnknownChange={(selected) =>
                  setEmbryoCountUnknown("numberDiscarded", selected)
                }
              />

              <MoonFormField
                label={t("cryo_vault.add_modal.embryo.number_frozen")}
                labelIcon={Snowflake}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    paddingVertical: 2,
                  }}
                >
                  <MoonStepper
                    value={e.numberFrozen ?? 0}
                    onChange={(v) => updateEmbryo({ numberFrozen: v })}
                    min={0}
                    editable
                    displayValue={e.numberFrozen == null ? "—" : undefined}
                    accentColor={accent}
                  />
                </View>
              </MoonFormField>

              <MoonFormField
                label={t("cryo_vault.add_modal.embryo.currently_in_storage")}
                labelIcon={Snowflake}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    paddingVertical: 2,
                  }}
                >
                  <MoonStepper
                    value={Math.max(
                      0,
                      e.currentlyInStorage ?? autoStorage ?? 0,
                    )}
                    onChange={(v) =>
                      updateEmbryo({
                        storageOverride: true,
                        currentlyInStorage: v,
                      })
                    }
                    min={0}
                    editable
                    displayValue={
                      e.currentlyInStorage == null && autoStorage == null
                        ? "—"
                        : undefined
                    }
                    accentColor={accent}
                  />
                </View>
              </MoonFormField>
            </View>
          ) : (
            /* Individual mode */
            <View style={{ gap: 8 }}>
              <DialogBodyText>
                {t("cryo_vault.add_modal.embryo.individual_development_hint")}
              </DialogBodyText>
              {e.individualEntries.map((entry, idx) => (
                <View key={entry.id} style={{ ...fieldBorder, padding: 12 }}>
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 11,
                        fontWeight: "600",
                        color: TEXT_MID,
                      }}
                    >
                      {t("cryo_vault.add_modal.embryo.embryo_index", {
                        params: { n: String(idx + 1) },
                      })}
                    </Text>
                    {e.individualEntries.length > 1 && (
                      <Pressable
                        onPress={() => {
                          capturePress(
                            pressLabel("remove_embryo_entry"),
                            "remove_embryo_entry",
                          );
                          removeIndividualEntry(entry.id);
                        }}
                      >
                        <Trash2 size={14} color={TEXT_FAINT} />
                      </Pressable>
                    )}
                  </View>
                  <View style={{ gap: 8 }}>
                    <View style={{ gap: 4 }}>
                      <Text
                        style={{
                          fontSize: 10,
                          color: TEXT_FAINT,
                          marginBottom: 1,
                        }}
                      >
                        {t(
                          "cryo_vault.add_modal.embryo.development_day_reached",
                        )}
                      </Text>
                      <EmbryoDayPicker
                        options={embryoDayOptions}
                        value={entry.dayOfDevelopment}
                        onChange={(v) =>
                          updateIndividualEntry(entry.id, {
                            dayOfDevelopment: v,
                          })
                        }
                        compact
                      />
                    </View>
                    <View style={{ flexDirection: "row", gap: 8 }}>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 10,
                            color: TEXT_FAINT,
                            marginBottom: 4,
                          }}
                        >
                          {t("cryo_vault.add_modal.embryo.embryo_id")}
                        </Text>
                        <TextInput
                          value={entry.embryoId}
                          onChangeText={(tv) =>
                            updateIndividualEntry(entry.id, { embryoId: tv })
                          }
                          placeholder={t(
                            "cryo_vault.add_modal.embryo.embryo_id_placeholder",
                          )}
                          placeholderTextColor={TEXT_FAINT}
                          style={{
                            ...fieldBorder,
                            paddingHorizontal: 10,
                            paddingVertical: 8,
                            fontSize: 12,
                            color: EMBER.colors.text,
                          }}
                        />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 10,
                            color: TEXT_FAINT,
                            marginBottom: 4,
                          }}
                        >
                          {t("cryo_vault.add_modal.embryo.grade")}
                        </Text>
                        <InlineDropdown
                          options={embryoGradeOptions}
                          value={entry.grade}
                          onChange={(v) =>
                            updateIndividualEntry(entry.id, { grade: v })
                          }
                          placeholder={t(
                            "cryo_vault.add_modal.embryo.grade_placeholder",
                          )}
                        />
                      </View>
                    </View>
                    <View style={{ flexDirection: "row", gap: 8 }}>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 10,
                            color: TEXT_FAINT,
                            marginBottom: 4,
                          }}
                        >
                          {t("cryo_vault.add_modal.embryo.genetic_status")}
                        </Text>
                        <InlineDropdown
                          options={geneticStatusOptions}
                          value={entry.geneticStatus}
                          onChange={(v) =>
                            updateIndividualEntry(entry.id, {
                              geneticStatus: v,
                            })
                          }
                          placeholder={t(
                            "cryo_vault.add_modal.embryo.genetic_status_placeholder",
                          )}
                        />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text
                          style={{
                            fontSize: 10,
                            color: TEXT_FAINT,
                            marginBottom: 4,
                          }}
                        >
                          {t("cryo_vault.add_modal.embryo.sex")}
                        </Text>
                        <InlineDropdown
                          options={sexOptions}
                          value={entry.sex}
                          onChange={(v) =>
                            updateIndividualEntry(entry.id, { sex: v })
                          }
                          placeholder={t(
                            "cryo_vault.add_modal.embryo.sex_placeholder",
                          )}
                        />
                      </View>
                    </View>
                    <View>
                      <Text
                        style={{
                          fontSize: 10,
                          color: TEXT_FAINT,
                          marginBottom: 4,
                        }}
                      >
                        {t("cryo_vault.add_modal.embryo.outcome")}
                      </Text>
                      <InlineDropdown
                        options={embryoStatusOptions}
                        value={entry.status}
                        onChange={(v) =>
                          updateIndividualEntry(entry.id, { status: v })
                        }
                      />
                    </View>
                    <TextInput
                      value={entry.notes}
                      onChangeText={(tv) =>
                        updateIndividualEntry(entry.id, { notes: tv })
                      }
                      placeholder={t(
                        "cryo_vault.add_modal.embryo.notes_placeholder",
                      )}
                      placeholderTextColor={TEXT_FAINT}
                      style={{
                        ...fieldBorder,
                        paddingHorizontal: 10,
                        paddingVertical: 8,
                        fontSize: 12,
                        color: EMBER.colors.text,
                      }}
                    />
                  </View>
                </View>
              ))}
              <Pressable
                onPress={() => {
                  capturePress(
                    pressLabel(t("cryo_vault.add_modal.embryo.add_embryo")),
                    "cryo_vault.add_modal.embryo.add_embryo",
                  );
                  addIndividualEntry();
                }}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  paddingVertical: 10,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderStyle: "dashed",
                  borderColor: BORDER,
                }}
              >
                <Plus size={14} color={TEXT_FAINT} />
                <Text style={{ fontSize: 12, color: TEXT_MID }}>
                  {t("cryo_vault.add_modal.embryo.add_embryo")}
                </Text>
              </Pressable>
            </View>
          )}
        </>
      )}
    </View>
  );
}

// ============================================================================
// Supporting documents
// ============================================================================

function DocumentUploadSection({
  files,
  onPick,
  onFilesSelected,
  onRemove,
  label,
  labelHint,
  compact = true,
  primaryLabel,
  secondaryLabel,
}: {
  files: FileAttachment[];
  onPick: () => void;
  onFilesSelected: (files: SelectedFile[]) => void;
  onRemove: (index: number) => void;
  label?: string;
  labelHint?: string;
  compact?: boolean;
  primaryLabel?: string;
  secondaryLabel?: string;
}) {
  const capturePress = usePressCapture("DocumentUploadSection");
  const t = useTranslations();
  return (
    <MoonFormField
      label={label ?? t("cryo_vault.add_modal.fields.documents")}
      labelIcon={FileText}
      labelHint={labelHint ?? t("cryo_vault.add_modal.fields.documents_prompt")}
    >
      {files.length > 0 && (
        <View style={{ gap: 4, marginBottom: 8 }}>
          {files.map((file, idx) => (
            <View
              key={`${file.name}-${idx}`}
              style={{
                ...fieldBorder,
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                paddingHorizontal: 12,
                paddingVertical: 8,
              }}
            >
              <FileText size={14} color={TEXT_FAINT} />
              <Text
                style={{ flex: 1, fontSize: 12, color: TEXT_MID }}
                numberOfLines={1}
              >
                {file.name}
              </Text>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={`Remove ${file.name}`}
                onPress={() => {
                  capturePress(
                    pressLabel(`Remove ${file.name}`),
                    "remove_specimen_file",
                  );
                  onRemove(idx);
                }}
              >
                <X size={14} color={TEXT_FAINT} />
              </Pressable>
            </View>
          ))}
        </View>
      )}
      <DocumentFileDropZone
        onPick={onPick}
        onFilesSelected={onFilesSelected}
        compact={compact}
        primaryLabel={
          primaryLabel ?? t("cryo_vault.add_modal.fields.documents_drop_action")
        }
        secondaryLabel={
          secondaryLabel ?? t("cryo_vault.add_modal.fields.documents_drop_hint")
        }
      />
    </MoonFormField>
  );
}

function SpecimenTypePicker({
  value,
  onChange,
}: {
  value: SpecimenFormType | null;
  onChange: (value: SpecimenFormType) => void;
}) {
  const capturePress = usePressCapture("SpecimenTypePicker");
  const t = useTranslations();
  const options: {
    key: SpecimenFormType;
    label: string;
    icon: LucideIcon;
  }[] = [
    {
      key: "eggs",
      label: t("cryo_vault.add_modal.type_toggle.eggs"),
      icon: Egg,
    },
    {
      key: "sperm",
      label: t("cryo_vault.add_modal.type_toggle.sperm"),
      icon: Droplets,
    },
    {
      key: "embryos",
      label: t("cryo_vault.add_modal.type_toggle.embryos"),
      icon: FlaskConical,
    },
  ];

  return (
    <View style={{ flexDirection: "row", gap: 8 }}>
      {options.map((option) => {
        const selected = value === option.key;
        const Icon = option.icon;
        return (
          <Pressable
            key={option.key}
            onPress={() => {
              capturePress(pressLabel(option.label), "specimen_option");
              onChange(option.key);
            }}
            accessibilityRole="button"
            accessibilityLabel={option.label}
            accessibilityState={{ selected }}
            aria-selected={selected}
            style={{
              flex: 1,
              alignItems: "center",
              paddingVertical: 12,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: selected
                ? `${SELECTION_ACCENT}30`
                : "rgba(0,0,0,0.06)",
              backgroundColor: selected
                ? `${SELECTION_ACCENT}18`
                : "transparent",
            }}
          >
            <Icon
              size={16}
              color={selected ? SELECTION_ACCENT : MOON_COLORS.textMid}
              strokeWidth={1.8}
              style={{ marginBottom: 5 }}
            />
            <Text
              style={{
                fontSize: 13,
                fontWeight: selected ? "600" : "400",
                color: selected ? SELECTION_ACCENT : TEXT_MID,
              }}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function SpecimenSourcePicker({
  value,
  onChange,
}: {
  value: SpecimenSource | null;
  onChange: (value: SpecimenSource) => void;
}) {
  const capturePress = usePressCapture("SpecimenSourcePicker");
  const t = useTranslations();
  const options: {
    key: SpecimenSource;
    label: string;
    icon: LucideIcon;
  }[] = [
    {
      key: "mine",
      label: t("cryo_vault.add_modal.source.mine"),
      icon: UserRound,
    },
    {
      key: "partner",
      label: t("cryo_vault.add_modal.source.partner"),
      icon: UsersRound,
    },
    {
      key: "donor",
      label: t("cryo_vault.add_modal.source.donor"),
      icon: HeartHandshake,
    },
    {
      key: "unknown",
      label: t("cryo_vault.add_modal.source.unknown"),
      icon: CircleHelp,
    },
  ];

  return (
    <View style={{ flexDirection: "row", gap: 8 }}>
      {options.map((option) => {
        const selected = value === option.key;
        const Icon = option.icon;
        return (
          <Pressable
            key={option.key}
            onPress={() => {
              capturePress(pressLabel(option.label), "specimen_option");
              onChange(option.key);
            }}
            accessibilityRole="button"
            accessibilityLabel={option.label}
            accessibilityState={{ selected }}
            aria-selected={selected}
            style={{
              flex: 1,
              alignItems: "center",
              paddingVertical: 11,
              borderRadius: 12,
              borderWidth: 1,
              borderColor: selected
                ? `${SELECTION_ACCENT}30`
                : "rgba(0,0,0,0.06)",
              backgroundColor: selected
                ? `${SELECTION_ACCENT}18`
                : "transparent",
            }}
          >
            <Icon
              size={16}
              color={selected ? SELECTION_ACCENT : MOON_COLORS.textMid}
              strokeWidth={1.8}
              style={{ marginBottom: 5 }}
            />
            <Text
              style={{
                fontSize: 13,
                fontWeight: selected ? "600" : "400",
                color: selected ? SELECTION_ACCENT : TEXT_MID,
              }}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function InlineOptionPicker({
  options,
  value,
  onChange,
  wrap = false,
  columns = 3,
}: {
  options: (DropdownOption & { detail?: string; icon?: LucideIcon })[];
  value: string;
  onChange: (value: string) => void;
  wrap?: boolean;
  columns?: 2 | 3;
}) {
  const capturePress = usePressCapture("InlineOptionPicker");
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: wrap ? "wrap" : "nowrap",
        gap: 8,
      }}
    >
      {options.map((option) => {
        const selected = option.id === value;
        const OptionIcon = option.icon;
        return (
          <Pressable
            key={option.id}
            onPress={() => {
              capturePress(pressLabel(option.label), "specimen_option");
              onChange(option.id);
            }}
            accessibilityRole="button"
            accessibilityLabel={option.label}
            accessibilityState={{ selected }}
            aria-selected={selected}
            style={{
              flex: wrap ? undefined : 1,
              flexGrow: wrap ? 1 : undefined,
              flexBasis: wrap ? (columns === 2 ? "46%" : "30%") : undefined,
              alignItems: "center",
              justifyContent: "center",
              minHeight: option.detail ? 58 : option.icon ? 48 : 42,
              minWidth: wrap ? 104 : undefined,
              paddingHorizontal: 8,
              paddingVertical: 8,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: selected
                ? `${SELECTION_ACCENT}30`
                : "rgba(0,0,0,0.06)",
              backgroundColor: selected
                ? `${SELECTION_ACCENT}18`
                : "transparent",
            }}
          >
            <View style={{ alignItems: "center", gap: 2 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                {OptionIcon && (
                  <OptionIcon
                    size={14}
                    color={selected ? SELECTION_ACCENT : MOON_COLORS.textMid}
                    strokeWidth={1.8}
                  />
                )}
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: selected ? "600" : "400",
                    color: selected ? SELECTION_ACCENT : TEXT_MID,
                    textAlign: "center",
                  }}
                >
                  {option.label}
                </Text>
              </View>
              {option.detail && (
                <Text
                  style={{
                    fontSize: 10,
                    color: selected ? SELECTION_ACCENT : TEXT_FAINT,
                    textAlign: "center",
                  }}
                >
                  {option.detail}
                </Text>
              )}
            </View>
          </Pressable>
        );
      })}
    </View>
  );
}

function AddPastCycleDialog({
  visible,
  specimenType,
  onClose,
  onSave,
}: {
  visible: boolean;
  specimenType: SpecimenFormType | null;
  onClose: () => void;
  onSave: (cycle: RelatedCycleReference) => void;
}) {
  const t = useTranslations();
  const [cycleType, setCycleType] = useState<CycleType | "">("");
  const [monthYear, setMonthYear] = useState("");

  useEffect(() => {
    if (!visible) return;
    setCycleType("");
    setMonthYear("");
  }, [specimenType, visible]);

  const cycleTypeOptions = useMemo(
    () =>
      (specimenType ? RELEVANT_CYCLE_TYPES[specimenType] : []).map((id) => {
        const labelKey =
          CYCLE_TYPE_LABEL_KEYS[id as keyof typeof CYCLE_TYPE_LABEL_KEYS];
        return {
          id,
          label: labelKey ? t(labelKey) : id,
        };
      }),
    [specimenType, t],
  );

  const currentYear = new Date().getFullYear();
  const monthYearMatch = /^(0[1-9]|1[0-2])\/(\d{4})$/.exec(monthYear);
  const year = monthYearMatch?.[2] ?? "";
  const isValidYear =
    Boolean(year) && Number(year) >= 1900 && Number(year) <= currentYear;
  const isValid = Boolean(cycleType && monthYearMatch && isValidYear);

  const handleMonthYearChange = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 6);
    setMonthYear(
      digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits,
    );
  };

  const handleSave = () => {
    if (!isValid || !monthYearMatch || !cycleType) return;
    const [, month, selectedYear] = monthYearMatch;
    onSave({
      id: `draft-past-cycle-${Date.now()}`,
      cycleType,
      startDate: `${selectedYear}-${month}-01`,
      status: "closed",
      isDraft: true,
    });
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      containerStyle={{ width: "90%", maxWidth: 440 }}
      contentStyle={{
        paddingTop: 28,
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
    >
      <DialogHeader
        title={t("cryo_vault.add_modal.past_cycle.title")}
        subtitle={t("cryo_vault.add_modal.past_cycle.subtitle")}
        titleStyle={{ textAlign: "center" }}
        subtitleStyle={{ textAlign: "center" }}
        style={{ alignItems: "center" }}
      />

      <View style={{ gap: 16, marginTop: 22 }}>
        <MoonFormField
          label={t("cryo_vault.add_modal.past_cycle.type_label")}
          labelIcon={FlaskConical}
        >
          <InlineOptionPicker
            options={cycleTypeOptions}
            value={cycleType}
            onChange={(value) => setCycleType(value as CycleType)}
            wrap
            columns={2}
          />
        </MoonFormField>

        <MoonFormField
          label={t("cryo_vault.add_modal.past_cycle.when_label")}
          labelIcon={CalendarDays}
        >
          <TextInput
            value={monthYear}
            onChangeText={handleMonthYearChange}
            placeholder={t("cryo_vault.add_modal.past_cycle.month_year")}
            placeholderTextColor={TEXT_FAINT}
            keyboardType="number-pad"
            inputMode="numeric"
            maxLength={7}
            accessibilityLabel={t("cryo_vault.add_modal.past_cycle.month_year")}
            style={{
              ...fieldBorder,
              paddingHorizontal: 12,
              paddingVertical: 10,
              fontSize: 13,
              color: EMBER.colors.text,
            }}
          />
        </MoonFormField>
      </View>

      <DialogActions style={{ marginTop: 24 }}>
        <SoftSecondaryButton
          label={t("cryo_vault.add_modal.past_cycle.cancel")}
          onPress={onClose}
          flex={1}
        />
        <DialogPrimaryButton
          label={t("cryo_vault.add_modal.past_cycle.save")}
          onPress={handleSave}
          disabled={!isValid}
          flex={1}
        />
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// CycleForm — flat form content rendered directly inside the dialog
// ============================================================================

function CycleForm({
  cycle,
  onUpdate,
  formStep,
  source,
  relatedCycleOptions,
  relatedCycleId,
  onRelatedCycleChange,
  onReportSelected,
}: {
  cycle: Cycle;
  onUpdate: (updates: Partial<Cycle>) => void;
  formStep: FormStep;
  source?: SpecimenSource | null;
  relatedCycleOptions: DropdownOption[];
  relatedCycleId: string | null;
  onRelatedCycleChange: (value: string) => void;
  onReportSelected: (file: SelectedFile) => void;
}) {
  const t = useTranslations();
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");

  const {
    predictions: locationPredictions,
    isSearching: isSearchingLocations,
  } = usePlacesPredictions({
    query: locationQuery,
    minChars: 1,
    debounceMs: 0,
    regionsOnly: true,
    enabled: locationPickerOpen,
  });

  const locationOptions = useMemo(
    () =>
      locationPredictions.map((prediction) => ({
        value: `place:${prediction.placeId}`,
        label: prediction.structuredFormatting.mainText,
        detail: prediction.structuredFormatting.secondaryText || undefined,
      })),
    [locationPredictions],
  );
  const fallbackLocationLabel =
    cycle.storageCountryName ||
    COUNTRIES.find((option) => option.id === cycle.country)?.label ||
    cycle.country;

  const handleLocationSearch = useCallback((query: string) => {
    setLocationQuery(query);
  }, []);

  const handleLocationChange = useCallback(
    async (value: string) => {
      if (!value.startsWith("place:")) return;
      const placeId = value.slice("place:".length);
      const details = await getPlaceDetails(placeId);
      const area = details ? extractPlaceArea(details) : null;
      if (!area) return;

      onUpdate({
        storageCity: area.label,
        country: area.countryCode,
        storageCountryName: area.countryName,
      });
    },
    [onUpdate],
  );

  const handleEntryModeChange = useCallback(
    (mode: Cycle["embryoEntryMode"]) => {
      if (mode === "individual") {
        const desiredCount = Math.max(
          0,
          cycle.embryo.totalEmbryosCreated ?? cycle.embryo.numberFrozen,
        );
        const entries =
          cycle.embryo.individualEntries.length >= desiredCount
            ? cycle.embryo.individualEntries
            : [
                ...cycle.embryo.individualEntries,
                ...Array.from(
                  {
                    length:
                      desiredCount - cycle.embryo.individualEntries.length,
                  },
                  () => createEmptyEmbryoEntry(),
                ),
              ];
        onUpdate({
          embryoEntryMode: mode,
          embryo: { ...cycle.embryo, individualEntries: entries },
        });
        return;
      }

      onUpdate({ embryoEntryMode: mode });
    },
    [cycle.embryo, onUpdate],
  );

  const handlePickFile = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });
      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0]!;
        onUpdate({
          files: [
            ...cycle.files,
            { name: asset.name, uri: asset.uri, mimeType: asset.mimeType },
          ],
        });
      }
    } catch {
      // silently ignore
    }
  }, [cycle.files, onUpdate]);

  const handleRemoveFile = useCallback(
    (idx: number) => {
      onUpdate({ files: cycle.files.filter((_, i) => i !== idx) });
    },
    [cycle.files, onUpdate],
  );

  const handleFilesSelected = useCallback(
    (selectedFiles: SelectedFile[]) => {
      if (selectedFiles.length === 0) return;
      onUpdate({
        files: [
          ...cycle.files,
          ...selectedFiles.map((file) => ({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          })),
        ],
      });
    },
    [cycle.files, onUpdate],
  );

  const handlePickReport = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.length) return;

      const asset = result.assets[0]!;
      onReportSelected({
        name: asset.name,
        uri: asset.uri,
        mimeType: resolveUploadContentType({
          name: asset.name,
          uri: asset.uri,
          mimeType: asset.mimeType,
        }),
      });
    } catch {
      // silently ignore
    }
  }, [onReportSelected]);

  const storageTypeOptions = useMemo(
    () =>
      STORAGE_LOCATION_TYPES.map((opt) => ({
        ...opt,
        label: t(`cryo_vault.add_modal.options.storage_type.${opt.id}`),
      })),
    [t],
  );
  const storageStateOptions = useMemo(
    () =>
      [
        {
          id: "stored" satisfies StorageState,
          label: t("cryo_vault.add_modal.fields.storage_state_stored"),
        },
        {
          id: "not_stored" satisfies StorageState,
          label: t("cryo_vault.add_modal.fields.storage_state_not_stored"),
        },
        {
          id: "unknown" satisfies StorageState,
          label: t("cryo_vault.add_modal.fields.storage_state_unknown"),
        },
      ] satisfies DropdownOption[],
    [t],
  );
  return (
    <View style={{ gap: 12 }}>
      {/* Associate the batch first, with an explicit duplicate signal. */}
      {formStep === "related" && source !== "donor" && source && (
        <MoonFormField
          label={
            cycle.type === "sperm"
              ? t("cryo_vault.add_modal.fields.sperm_related_cycle_optional")
              : t("cryo_vault.add_modal.fields.related_cycle_optional")
          }
          labelIcon={CalendarDays}
        >
          <InlineOptionPicker
            options={relatedCycleOptions}
            value={relatedCycleId ?? ""}
            onChange={onRelatedCycleChange}
            wrap
          />
          {(relatedCycleId === "preservation" ||
            relatedCycleId === UNLINKED_SPECIMEN_CYCLE_ID) && (
            <Text
              style={{
                color: TEXT_MID,
                fontSize: 12,
                lineHeight: 17,
                marginTop: 2,
              }}
            >
              {t("cryo_vault.add_modal.fields.related_cycle_not_linked_hint")}
            </Text>
          )}
        </MoonFormField>
      )}

      {/* Egg, sperm, and embryo entry each use focused steps so the form can
          keep clinical details separate from storage and record keeping. */}
      {cycle.type === "eggs" && formStep === "counts" && (
        <OocyteFieldsSection
          cycle={cycle}
          onUpdate={onUpdate}
          section="counts"
        />
      )}
      {cycle.type === "eggs" && formStep === "usage" && (
        <OocyteFieldsSection
          cycle={cycle}
          onUpdate={onUpdate}
          section="usage"
        />
      )}
      {cycle.type === "eggs" && formStep === "details" && (
        <OocyteFieldsSection
          cycle={cycle}
          onUpdate={onUpdate}
          section="details"
        />
      )}
      {cycle.type === "sperm" && formStep === "analysis" && (
        <SemenFieldsSection
          cycle={cycle}
          onUpdate={onUpdate}
          section="analysis"
        />
      )}
      {cycle.type === "sperm" && formStep === "counts" && (
        <SemenFieldsSection
          cycle={cycle}
          onUpdate={onUpdate}
          section="counts"
        />
      )}
      {cycle.type === "embryos" &&
        (formStep === "development" || formStep === "details") && (
          <EmbryoEntryModeTabs cycle={cycle} onChange={handleEntryModeChange} />
        )}
      {cycle.type === "embryos" && formStep === "development" && (
        <EmbryoFieldsSection
          cycle={cycle}
          onUpdate={onUpdate}
          section="development"
        />
      )}
      {cycle.type === "embryos" && formStep === "inventory" && (
        <>
          <DocumentUploadSection
            files={cycle.files}
            onPick={() => void handlePickReport()}
            onFilesSelected={(files) => {
              const file = files[0];
              if (file) onReportSelected(file);
            }}
            onRemove={(index) =>
              onUpdate({
                files: cycle.files.filter(
                  (_, fileIndex) => fileIndex !== index,
                ),
              })
            }
            label={t("cryo_vault.add_modal.fields.embryo_report")}
            labelHint={t("cryo_vault.add_modal.fields.embryo_report_prompt")}
            compact
            primaryLabel={t(
              "cryo_vault.add_modal.fields.embryo_report_drop_action",
            )}
            secondaryLabel={t(
              "cryo_vault.add_modal.fields.embryo_report_drop_hint",
            )}
          />
          <EmbryoEntryModeTabs cycle={cycle} onChange={handleEntryModeChange} />
          <EmbryoFieldsSection
            cycle={cycle}
            onUpdate={onUpdate}
            section="inventory"
          />
        </>
      )}

      {formStep === "details" && (
        <>
          <MoonFormField
            label={t("cryo_vault.add_modal.source.who_specimen_from_optional")}
            labelIcon={UserRound}
          >
            <SpecimenSourcePicker
              value={source ?? null}
              onChange={(value) => onUpdate({ source: value })}
            />
          </MoonFormField>

          <MoonFormField
            label={t("cryo_vault.add_modal.fields.storage_state_optional")}
            labelIcon={Snowflake}
          >
            <InlineOptionPicker
              options={storageStateOptions}
              value={cycle.storageState ?? ""}
              onChange={(value) =>
                onUpdate({ storageState: value as StorageState })
              }
              wrap
            />
          </MoonFormField>

          {/* Facility and storage metadata are supplemental. Clinical
              information and embryo inventory are saved independently. */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-start",
              gap: 10,
              zIndex: 5,
            }}
          >
            <MoonFormField
              label={t("cryo_vault.add_modal.fields.stored_at_optional")}
              labelIcon={MapPin}
              style={{ flex: 1 }}
            >
              <ClinicPicker
                value={cycle.clinicName}
                onChange={(v) => onUpdate({ clinicName: v })}
              />
            </MoonFormField>

            <MoonFormField
              label={t("cryo_vault.add_modal.fields.storage_location")}
              labelIcon={MapPin}
              style={{ width: 152, zIndex: locationPickerOpen ? 20 : 1 }}
            >
              <PillSelect
                value=""
                options={locationOptions}
                onChange={(value) => void handleLocationChange(value)}
                accessibilityLabel="Location"
                displayValue={
                  cycle.storageCity || fallbackLocationLabel || undefined
                }
                placeholder={
                  cycle.storageCity ||
                  fallbackLocationLabel ||
                  t("cryo_vault.add_modal.fields.storage_location_placeholder")
                }
                searchable
                searchPlaceholder={t(
                  "cryo_vault.add_modal.fields.search_location",
                )}
                onSearch={handleLocationSearch}
                searching={isSearchingLocations}
                filterOptionsLocally={false}
                emptyLabel={t(
                  "cryo_vault.add_modal.fields.location_no_matches",
                )}
                surface="field"
                open={locationPickerOpen}
                onOpenChange={(open) => {
                  setLocationPickerOpen(open);
                  if (!open) setLocationQuery("");
                }}
                style={{ width: "100%" }}
              />
            </MoonFormField>
          </View>

          <View style={{ flexDirection: "row", gap: 10 }}>
            <MoonFormField
              label={t("cryo_vault.add_modal.fields.storage_type_optional")}
              labelIcon={MapPin}
              style={{ flex: 1 }}
            >
              <InlineDropdown
                options={storageTypeOptions}
                value={cycle.storageLocationType}
                onChange={(v) => onUpdate({ storageLocationType: v })}
              />
            </MoonFormField>

            <MoonFormField
              label={t("cryo_vault.add_modal.fields.storage_id_optional")}
              labelIcon={MapPin}
              style={{ flex: 1 }}
            >
              <TextInput
                value={cycle.storageId}
                onChangeText={(value) => onUpdate({ storageId: value })}
                placeholder={t(
                  "cryo_vault.add_modal.fields.storage_id_placeholder",
                )}
                placeholderTextColor={TEXT_FAINT}
                style={{
                  ...fieldBorder,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  fontSize: 13,
                  color: EMBER.colors.text,
                }}
              />
            </MoonFormField>
          </View>

          <DocumentUploadSection
            files={cycle.files}
            onPick={handlePickFile}
            onFilesSelected={handleFilesSelected}
            onRemove={handleRemoveFile}
          />

          {/* Notes */}
          <MoonFormField
            label={t("cryo_vault.add_modal.fields.notes")}
            labelIcon={FileText}
          >
            <TextInput
              value={cycle.notes}
              onChangeText={(tv) => onUpdate({ notes: tv })}
              placeholder={t("cryo_vault.add_modal.fields.notes_placeholder")}
              placeholderTextColor={TEXT_FAINT}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
              style={{
                ...fieldBorder,
                paddingHorizontal: 12,
                paddingVertical: 10,
                fontSize: 13,
                color: EMBER.colors.text,
                minHeight: 64,
              }}
            />
          </MoonFormField>
        </>
      )}
    </View>
  );
}

function SpecimenAddedSuccess({
  specimenType,
  hasFertilizedEmbryos,
  onAddEmbryos,
  onAddAnotherSample,
  onDismiss,
}: {
  specimenType: SpecimenFormType;
  hasFertilizedEmbryos: boolean;
  onAddEmbryos: () => void;
  onAddAnotherSample: () => void;
  onDismiss: () => void;
}) {
  const t = useTranslations();
  const progress = useSharedValue(0);
  const illustration = SPECIMEN_ILLUSTRATIONS[specimenType];

  useEffect(() => {
    progress.value = withDelay(
      220,
      withTiming(1, {
        duration: 1050,
        easing: Easing.inOut(Easing.cubic),
      }),
    );
  }, [progress]);

  const specimenStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 0.72, 1], [1, 1, 0.22]),
    transform: [
      { translateX: interpolate(progress.value, [0, 1], [0, 52]) },
      { translateY: interpolate(progress.value, [0, 1], [0, 92]) },
      { scale: interpolate(progress.value, [0, 1], [1, 0.38]) },
    ],
  }));

  const fridgeStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 0.45, 1], [0.7, 0.88, 1]),
    transform: [{ scale: interpolate(progress.value, [0, 1], [0.9, 1]) }],
  }));

  const confirmationStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0.55, 1], [0, 1]),
    transform: [{ translateY: interpolate(progress.value, [0.55, 1], [8, 0]) }],
  }));

  const specimenLabel = t(`cryo_vault.add_modal.cycle_card.${specimenType}`);
  const successTitle =
    specimenType === "eggs"
      ? t("cryo_vault.add_modal.success_title_eggs")
      : t("cryo_vault.add_modal.success_title", {
          params: { type: specimenLabel },
        });

  return (
    <View
      style={{
        alignItems: "center",
        paddingHorizontal: 24,
        paddingBottom: 28,
      }}
    >
      <View
        style={{
          width: "100%",
          height: 152,
          alignItems: "center",
          justifyContent: "flex-start",
          position: "relative",
          marginBottom: 10,
        }}
      >
        <Animated.Image
          source={illustration.source}
          resizeMode="contain"
          style={[
            {
              position: "absolute",
              top: 4,
              width: Math.min(illustration.width, 130),
              height: Math.min(illustration.height, 96),
            },
            specimenStyle,
          ]}
        />
        <Animated.Image
          source={CRYO_VAULT_ILLUSTRATION}
          resizeMode="contain"
          style={[
            { position: "absolute", bottom: 0, width: 116, height: 116 },
            fridgeStyle,
          ]}
        />
      </View>
      <Animated.View style={[{ alignItems: "center" }, confirmationStyle]}>
        <DialogHeader
          title={successTitle}
          subtitle={
            hasFertilizedEmbryos
              ? undefined
              : t("cryo_vault.add_modal.success_body")
          }
          style={{ alignItems: "center" }}
          titleStyle={{ textAlign: "center" }}
          subtitleStyle={{ textAlign: "center" }}
        />
      </Animated.View>
      {hasFertilizedEmbryos && (
        <Animated.View
          style={[
            {
              width: "100%",
              alignItems: "center",
              gap: 12,
              marginTop: 18,
            },
            confirmationStyle,
          ]}
        >
          <DialogBodyText style={{ textAlign: "center" }}>
            {t("cryo_vault.add_modal.embryo_follow_up.prompt")}
          </DialogBodyText>
          <DialogActions style={{ width: "100%" }}>
            <SoftSecondaryButton
              label={t("cryo_vault.add_modal.embryo_follow_up.not_now")}
              onPress={onDismiss}
              flex={1}
            />
            <DialogPrimaryButton
              label={t("cryo_vault.add_modal.embryo_follow_up.add_details")}
              onPress={onAddEmbryos}
              flex={1}
            />
          </DialogActions>
        </Animated.View>
      )}
      {!hasFertilizedEmbryos && (
        <Animated.View
          style={[
            {
              width: "100%",
              alignItems: "center",
              gap: 12,
              marginTop: 18,
            },
            confirmationStyle,
          ]}
        >
          <DialogBodyText style={{ textAlign: "center" }}>
            {t("cryo_vault.add_modal.add_another_sample_prompt")}
          </DialogBodyText>
          <DialogActions style={{ width: "100%" }}>
            <SoftSecondaryButton
              label={t("cryo_vault.add_modal.done")}
              onPress={onDismiss}
              flex={1}
            />
            <DialogPrimaryButton
              label={t("cryo_vault.add_modal.add_another_sample_action")}
              onPress={onAddAnotherSample}
              flex={1}
            />
          </DialogActions>
        </Animated.View>
      )}
    </View>
  );
}

// ============================================================================
// Main Modal
// ============================================================================

function buildPrefillCycle(prefill: SpecimenPrefill, country = "US"): Cycle {
  const c = createCycleWithCountry(country);
  if (prefill.type) c.type = prefill.type;
  if (prefill.clinicName) c.clinicName = prefill.clinicName;
  if (prefill.storageLocation) c.storageId = prefill.storageLocation;

  if (prefill.type === "embryos") {
    if (prefill.freezeDate) c.embryo.freezeDate = prefill.freezeDate;
    if (prefill.numberFrozen != null) {
      c.embryo.numberFrozen = prefill.numberFrozen;
    }
    if (prefill.embryoDayOfDevelopment) {
      c.embryo.dayOfDevelopment = prefill.embryoDayOfDevelopment;
    }
    if (prefill.fertilizationMethod) {
      c.embryo.fertilisationMethod = prefill.fertilizationMethod;
    }
    if (prefill.numberTested != null) {
      c.embryo.numberTested = prefill.numberTested;
    }
    if (prefill.numberEuploid != null) {
      c.embryo.numberEuploid = prefill.numberEuploid;
    }
    if (prefill.numberAneuploid != null) {
      c.embryo.numberAneuploid = prefill.numberAneuploid;
    }
    if (prefill.numberMosaic != null) {
      c.embryo.numberMosaic = prefill.numberMosaic;
    }
    if (prefill.numberNoResult != null) {
      c.embryo.numberNoResult = prefill.numberNoResult;
    }
    if (prefill.retrievalDate) {
      c.embryo.fertilisationDate = prefill.retrievalDate;
    }
  } else if (prefill.type === "sperm") {
    if (prefill.collectedAt) c.sperm.collectedAt = prefill.collectedAt;
    if (prefill.freezeDate) c.sperm.freezeDate = prefill.freezeDate;
    if (prefill.numberFrozen != null) {
      c.sperm.numberFrozen = prefill.numberFrozen;
    }
    if (prefill.abstinencePeriod != null) {
      c.sperm.abstinencePeriod = prefill.abstinencePeriod;
    }
    if (prefill.concentration != null) {
      c.sperm.concentration = prefill.concentration;
    }
    if (prefill.totalCount != null) c.sperm.totalCount = prefill.totalCount;
    if (prefill.motilityPercent != null) {
      c.sperm.motilityPercent = prefill.motilityPercent;
    }
    if (prefill.morphologyPercent != null) {
      c.sperm.morphologyPercent = prefill.morphologyPercent;
    }
    if (prefill.volumeMl != null) c.sperm.volumeMl = prefill.volumeMl;
  } else {
    if (prefill.retrievalDate) c.oocyte.retrievalDate = prefill.retrievalDate;
    if (prefill.freezeDate) c.oocyte.freezeDate = prefill.freezeDate;
    if (prefill.totalRetrieved != null) {
      c.oocyte.totalRetrieved = prefill.totalRetrieved;
    }
    if (prefill.matureMII != null) c.oocyte.matureMII = prefill.matureMII;
    if (prefill.numberFrozen != null) {
      c.oocyte.numberFrozen = prefill.numberFrozen;
    }
    if (prefill.numberFertilized != null) {
      c.oocyte.numberFertilized = prefill.numberFertilized;
    }
    if (prefill.cohortDisposition) {
      c.oocyte.cohortDisposition = prefill.cohortDisposition;
    }
  }
  return c;
}

function buildSavedPrefill(cycle: Cycle): SpecimenPrefill {
  if (cycle.type === "sperm") {
    return {
      type: "sperm",
      clinicName: cycle.clinicName,
      collectedAt: cycle.sperm.collectedAt,
      freezeDate: cycle.sperm.freezeDate,
      numberFrozen: cycle.sperm.numberFrozen,
      abstinencePeriod: cycle.sperm.abstinencePeriod ?? undefined,
      concentration: cycle.sperm.concentration ?? undefined,
      totalCount: cycle.sperm.totalCount ?? undefined,
      motilityPercent: cycle.sperm.motilityPercent ?? undefined,
      morphologyPercent: cycle.sperm.morphologyPercent ?? undefined,
      volumeMl: cycle.sperm.volumeMl ?? undefined,
    };
  }

  if (cycle.type === "embryos") {
    return {
      type: "embryos",
      clinicName: cycle.clinicName,
      retrievalDate: cycle.embryo.fertilisationDate,
      freezeDate: cycle.embryo.freezeDate,
      numberFrozen: cycle.embryo.numberFrozen,
      fertilizationMethod: cycle.embryo.fertilisationMethod,
      embryoGrade: cycle.embryo.individualEntries[0]?.grade,
    };
  }

  return {
    type: "eggs",
    clinicName: cycle.clinicName,
    retrievalDate: cycle.oocyte.retrievalDate,
    freezeDate: cycle.oocyte.freezeDate,
    totalRetrieved: cycle.oocyte.totalRetrieved ?? undefined,
    matureMII: cycle.oocyte.matureMII ?? undefined,
    numberFrozen: cycle.oocyte.numberFrozen,
    numberFertilized: cycle.oocyte.numberFertilized ?? undefined,
    cohortDisposition: cycle.oocyte.cohortDisposition ?? undefined,
    storageLocation: cycle.storageId,
  };
}

export function AddSpecimenModal({
  visible,
  onClose,
  cycleId,
  prefill,
  onSaved,
  initialFile,
}: Props) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const { tenantId, patientId } = useApiClient();
  const { data: patientCycles } = useCycles();
  const { data: patientSpecimens } = useSpecimens();
  const { save: saveCanonicalCycle } = useCanonicalCycleIntake();
  const { height: windowHeight } = useWindowDimensions();
  const [deviceCountry] = useState(getDeviceCountryCode);
  const defaultCountry =
    normalizeCountryCode(patient?.addresses?.[0]?.country) ?? deviceCountry;
  const [step, setStep] = useState<ModalStep>("upload");
  const [manualType, setManualType] = useState<SpecimenFormType | null>(null);
  const [manualSource, setManualSource] = useState<SpecimenSource | null>(null);
  const [relatedCycleId, setRelatedCycleId] = useState<string | null>(null);
  const [customRelatedCycles, setCustomRelatedCycles] = useState<
    RelatedCycleReference[]
  >([]);
  const [isPastCycleDialogVisible, setIsPastCycleDialogVisible] =
    useState(false);
  /**
   * Which step she is on, held as the step's own identity rather than its rank.
   *
   * `formSteps` is not a fixed list: answering "whose sample is this?" with
   * "mine" or "partner" makes the related-cycle step appear, and it appears at
   * the *front*. A rank therefore addressed a different step after the answer
   * than before it — every later step shifted by one — so her first tap on
   * "Le mien" threw her back a step, and only the second one, which changed the
   * list no further, behaved. Naming the step removes the whole class: the step
   * she is on stays the step she is on, whatever the list does around it.
   *
   * `null` means the first step, which is also where every reset lands.
   */
  const [formStepId, setFormStepId] = useState<FormStep | null>(null);
  const [cycles, setCycles] = useState<Cycle[]>(() => [
    createCycleWithCountry(deviceCountry),
  ]);
  const [isParsing, setIsParsing] = useState(false);
  const [failedUpload, setFailedUpload] = useState<{
    name: string;
    uri: string;
    mimeType?: string | null;
    uploadId?: string;
  } | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingSpecimenIdempotencyKeys, setPendingSpecimenIdempotencyKeys] =
    useState<Record<string, string> | null>(null);
  const [pendingSpecimenSampleIds, setPendingSpecimenSampleIds] =
    useState<Record<string, string> | null>(null);
  const [pendingSpecimenOperationKey, setPendingSpecimenOperationKey] =
    useState<string | null>(null);
  const [pendingPostSavePrefill, setPendingPostSavePrefill] =
    useState<SpecimenPrefill | null>(null);
  const [pendingDocumentCycles, setPendingDocumentCycles] = useState<
    Cycle[] | null
  >(null);
  const initialFileKeyRef = useRef<string | null>(null);
  const prefillInitializedRef = useRef(false);

  const createSpecimen = useCreateSpecimen();
  const transfer = useDocumentSourceTransfer();

  const processUploadFile = useCallback(
    async (
      asset: {
        name: string;
        uri: string;
        mimeType?: string | null;
        uploadId?: string;
      },
      targetCycleId?: string,
    ) => {
      setIsParsing(true);
      setFailedUpload(null);
      let uploadedSourceId = asset.uploadId;

      try {
        if (!tenantId || !patientId) {
          throw new Error("Missing tenant or patient ID");
        }
        const source = asset.uploadId
          ? { file: asset, uploadId: asset.uploadId }
          : await transfer.uploadSource(asset, { destination: "specimen" });
        uploadedSourceId = source.uploadId;
        const extracted = projectDocumentProcessingRunToPrefill(
          (
            await transfer.processSource(source, {
              attempts: 24,
              intervalMs: 1_250,
            })
          ).run,
          asset.name,
        );

        // Build a new cycle for the initial upload, or merge the report into
        // the existing embryo cycle when the report was added from inventory.
        const existingCycle = targetCycleId
          ? cycles.find((cycle) => cycle.id === targetCycleId)
          : undefined;
        const newCycle = existingCycle
          ? {
              ...existingCycle,
              oocyte: { ...existingCycle.oocyte },
              embryo: {
                ...existingCycle.embryo,
                individualEntries: existingCycle.embryo.individualEntries.map(
                  (entry) => ({ ...entry }),
                ),
              },
              sperm: { ...existingCycle.sperm },
              files: [...existingCycle.files],
            }
          : createCycleWithCountry(defaultCountry);
        const d = extracted.specimen;

        if (!targetCycleId && d.specimenType === "embryos") {
          newCycle.type = "embryos";
        }
        if (!targetCycleId && d.specimenType === "sperm") {
          newCycle.type = "sperm";
        }
        if (typeof d.clinicName === "string") {
          newCycle.clinicName = d.clinicName;
        }
        if (typeof d.country === "string") newCycle.country = d.country;
        if (typeof d.storageId === "string") newCycle.storageId = d.storageId;

        // Sperm fields
        if (newCycle.type === "sperm") {
          const collectionDate =
            typeof d.collectedAt === "string"
              ? d.collectedAt
              : typeof d.retrievalDate === "string"
                ? d.retrievalDate
                : "";
          if (collectionDate) newCycle.sperm.collectedAt = collectionDate;
          if (typeof d.freezeDate === "string") {
            newCycle.sperm.freezeDate = d.freezeDate;
          }
          if (typeof d.numberFrozen === "number") {
            newCycle.sperm.numberFrozen = Math.max(0, d.numberFrozen);
          }
          if (typeof d.collectionMethod === "string") {
            newCycle.sperm.collectionMethod = d.collectionMethod;
          }
          if (typeof d.processingMethod === "string") {
            newCycle.sperm.processingMethod = d.processingMethod;
          }
          if (typeof d.abstinencePeriod === "number") {
            newCycle.sperm.abstinencePeriod = d.abstinencePeriod;
          }
          if (typeof d.concentration === "number") {
            newCycle.sperm.concentration = d.concentration;
          }
          if (typeof d.totalCount === "number") {
            newCycle.sperm.totalCount = d.totalCount;
          }
          if (typeof d.motilityPercent === "number") {
            newCycle.sperm.motilityPercent = d.motilityPercent;
          }
          if (typeof d.morphologyPercent === "number") {
            newCycle.sperm.morphologyPercent = d.morphologyPercent;
          }
          if (typeof d.volumeMl === "number") {
            newCycle.sperm.volumeMl = d.volumeMl;
          }
        }

        // Oocyte fields
        if (typeof d.retrievalDate === "string") {
          newCycle.oocyte.retrievalDate = d.retrievalDate;
        }
        if (typeof d.freezeDate === "string") {
          newCycle.oocyte.freezeDate = d.freezeDate;
          newCycle.embryo.freezeDate = d.freezeDate;
        }
        if (typeof d.freezingMethod === "string") {
          newCycle.oocyte.freezingMethod = d.freezingMethod;
        }
        if (typeof d.numberFrozen === "number") {
          newCycle.oocyte.numberFrozen = d.numberFrozen;
          newCycle.embryo.numberFrozen = d.numberFrozen;
        }
        if (typeof d.totalRetrieved === "number") {
          newCycle.oocyte.totalRetrieved = d.totalRetrieved;
        }
        if (typeof d.matureMII === "number") {
          newCycle.oocyte.matureMII = d.matureMII;
        }
        if (typeof d.numberFertilized === "number") {
          newCycle.oocyte.numberFertilized = d.numberFertilized;
        }
        if (
          d.cohortDisposition === "all_frozen" ||
          d.cohortDisposition === "mixed_frozen_fertilized" ||
          d.cohortDisposition === "all_fertilized" ||
          d.cohortDisposition === "unknown"
        ) {
          newCycle.oocyte.cohortDisposition = d.cohortDisposition;
        }

        // Embryo fields
        if (typeof d.dayOfDevelopment === "string") {
          newCycle.embryo.dayOfDevelopment = d.dayOfDevelopment;
        }
        if (typeof d.fertilisationMethod === "string") {
          newCycle.embryo.fertilisationMethod = d.fertilisationMethod;
        }
        if (typeof d.pgtPerformed === "string") {
          newCycle.embryo.pgtPerformed = d.pgtPerformed;
        }
        if (typeof d.totalEmbryosCreated === "number") {
          newCycle.embryo.totalEmbryosCreated = d.totalEmbryosCreated;
        }
        if (typeof d.numberTested === "number") {
          newCycle.embryo.numberTested = d.numberTested;
        }
        if (typeof d.numberEuploid === "number") {
          newCycle.embryo.numberEuploid = d.numberEuploid;
        }
        if (typeof d.numberAneuploid === "number") {
          newCycle.embryo.numberAneuploid = d.numberAneuploid;
        }
        if (typeof d.numberMosaic === "number") {
          newCycle.embryo.numberMosaic = d.numberMosaic;
        }
        if (typeof d.numberNoResult === "number") {
          newCycle.embryo.numberNoResult = d.numberNoResult;
        }

        // Individual embryos from OCR
        if (Array.isArray(d.embryos) && d.embryos.length > 0) {
          newCycle.embryoEntryMode = "individual";
          newCycle.embryo.individualEntries = d.embryos.map((em) => ({
            ...createEmptyEmbryoEntry(),
            embryoId: typeof em.embryoId === "string" ? em.embryoId : "",
            dayOfDevelopment:
              typeof em.dayOfDevelopment === "string"
                ? em.dayOfDevelopment
                : newCycle.embryo.dayOfDevelopment,
            grade: typeof em.grade === "string" ? em.grade : "",
            geneticStatus:
              typeof em.geneticStatus === "string" ? em.geneticStatus : "",
            sex: typeof em.sex === "string" ? em.sex : "",
            status: typeof em.status === "string" ? em.status : "",
          }));
        }

        // Attach the uploaded file to the cycle that was parsed.
        newCycle.files = [
          ...newCycle.files.filter((file) => file.uri !== asset.uri),
          {
            name: asset.name,
            uri: asset.uri,
            mimeType: asset.mimeType ?? undefined,
            uploadId: uploadedSourceId,
          },
        ];

        if (targetCycleId) {
          setCycles((previous) =>
            previous.map((cycle) =>
              cycle.id === targetCycleId ? newCycle : cycle,
            ),
          );
        } else {
          setCycles([newCycle]);
          setManualType(newCycle.type);
          setManualSource(null);
          setRelatedCycleId(null);
        }
      } catch {
        // OCR failed — keep the report attached, surface the failure, and let
        // the patient retry extraction instead of silently treating the file
        // as successfully imported.
        setFailedUpload({ ...asset, uploadId: uploadedSourceId });
        if (targetCycleId) {
          setCycles((previous) =>
            previous.map((cycle) =>
              cycle.id === targetCycleId
                ? {
                    ...cycle,
                    files: [
                      ...cycle.files,
                      {
                        name: asset.name,
                        uri: asset.uri,
                        mimeType: asset.mimeType ?? undefined,
                        uploadId: uploadedSourceId,
                      },
                    ],
                  }
                : cycle,
            ),
          );
        } else {
          const newCycle = createCycleWithCountry(defaultCountry);
          newCycle.files = [
            {
              name: asset.name,
              uri: asset.uri,
              mimeType: asset.mimeType ?? undefined,
              uploadId: uploadedSourceId,
            },
          ];
          setCycles([newCycle]);
          setManualType(null);
          setManualSource(null);
          setRelatedCycleId(null);
        }
      } finally {
        setIsParsing(false);
        if (!targetCycleId) setStep("upload");
      }
    },
    [cycles, defaultCountry, patientId, tenantId, transfer],
  );

  // Auto-enter form with prefill when modal opens
  useEffect(() => {
    if (!visible) {
      prefillInitializedRef.current = false;
      return;
    }
    if (
      visible &&
      prefill &&
      !initialFile &&
      step === "upload" &&
      !prefillInitializedRef.current
    ) {
      prefillInitializedRef.current = true;
      const c = buildPrefillCycle(prefill, defaultCountry);
      setCycles([c]);
      // A typed prefill is already associated with the active cycle, so keep
      // the source unset (which omits the related-cycle picker) but still seed
      // the form type. Without this, formSteps only contains "details" and
      // the retrieval counts/usage pages are silently skipped.
      setManualType(prefill.type ?? "eggs");
      setManualSource(null);
      setFormStepId(null);
      setRelatedCycleId(null);
      setStep("form");
    }
  }, [defaultCountry, visible, prefill, initialFile, step]);

  useEffect(() => {
    if (!visible || !initialFile || step !== "upload") return;
    const fileKey = `${initialFile.uri}:${initialFile.name}`;
    if (initialFileKeyRef.current === fileKey) return;
    initialFileKeyRef.current = fileKey;
    void processUploadFile(initialFile);
  }, [visible, initialFile, step, processUploadFile]);

  // Reset on close
  const handleClose = useCallback(() => {
    if (saveState === "saving") return;
    setCycles([createCycleWithCountry(defaultCountry)]);
    setStep("upload");
    setManualType(null);
    setManualSource(null);
    setRelatedCycleId(null);
    setFormStepId(null);
    setCustomRelatedCycles([]);
    setIsPastCycleDialogVisible(false);
    setSaveState("idle");
    setErrorMessage("");
    setPendingSpecimenIdempotencyKeys(null);
    setPendingSpecimenOperationKey(null);
    setPendingPostSavePrefill(null);
    setPendingDocumentCycles(null);
    setFailedUpload(null);
    setIsParsing(false);
    initialFileKeyRef.current = null;
    prefillInitializedRef.current = false;
    onClose();
  }, [defaultCountry, saveState, onClose]);

  const handleAddAnotherSample = useCallback(() => {
    if (saveState === "saving") return;
    setCycles([createCycleWithCountry(defaultCountry)]);
    setStep("upload");
    setManualType(null);
    setManualSource(null);
    setRelatedCycleId(null);
    setFormStepId(null);
    setCustomRelatedCycles([]);
    setIsPastCycleDialogVisible(false);
    setSaveState("idle");
    setErrorMessage("");
    setPendingSpecimenIdempotencyKeys(null);
    setPendingSpecimenOperationKey(null);
    setPendingPostSavePrefill(null);
    setPendingDocumentCycles(null);
    setFailedUpload(null);
    setIsParsing(false);
    initialFileKeyRef.current = null;
    prefillInitializedRef.current = true;
  }, [defaultCountry, saveState]);

  // OCR document upload
  const handlePickDocument = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.length) return;

      const asset = result.assets[0]!;
      await processUploadFile({
        name: asset.name,
        uri: asset.uri,
        mimeType: asset.mimeType,
      });
    } catch {
      setIsParsing(false);
    }
  }, [processUploadFile]);

  const handlePickSupportingDocument = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });
      if (result.canceled || !result.assets?.length) return;

      const asset = result.assets[0]!;
      setCycles((previous) =>
        previous.map((cycle, index) =>
          index === 0
            ? {
                ...cycle,
                files: [
                  ...cycle.files,
                  {
                    name: asset.name,
                    uri: asset.uri,
                    mimeType: asset.mimeType ?? undefined,
                  },
                ],
              }
            : cycle,
        ),
      );
    } catch {
      // silently ignore
    }
  }, []);

  const handleManualNext = useCallback(() => {
    if (!manualType || !manualSource) return;
    const existingCycle = cycles[0];
    const c = existingCycle?.files.length
      ? { ...existingCycle, type: manualType }
      : prefill
        ? buildPrefillCycle(prefill, defaultCountry)
        : createCycleWithCountry(defaultCountry);
    c.type = manualType;
    c.source = manualSource;
    if (manualType === "sperm") {
      c.sperm.origin = manualSource;
      c.sperm.isDonor = manualSource === "donor";
    }
    setCycles([c]);
    setFormStepId(null);
    setStep("form");
  }, [cycles, defaultCountry, manualSource, manualType, prefill]);

  const handleAddResultingEmbryos = useCallback(() => {
    setCycles((previous) =>
      previous.map((cycle, index) => {
        if (index !== 0 || cycle.type !== "eggs") return cycle;

        return {
          ...cycle,
          type: "embryos",
          embryo: {
            ...cycle.embryo,
            fertilisationDate: cycle.oocyte.retrievalDate,
            totalEmbryosCreated:
              cycle.oocyte.numberFertilized ?? cycle.oocyte.matureMII ?? 0,
          },
        };
      }),
    );
    setManualType("embryos");
    setFormStepId(null);
    setSaveState("idle");
    setErrorMessage("");
    setPendingSpecimenIdempotencyKeys(null);
    setPendingSpecimenOperationKey(null);
  }, []);

  const handleUpdateCycle = useCallback(
    (cycleIdx: string, updates: Partial<Cycle>) => {
      setCycles((prev) =>
        prev.map((c) => (c.id === cycleIdx ? { ...c, ...updates } : c)),
      );
    },
    [],
  );

  const handleRelatedCycleChange = useCallback(
    (value: string) => {
      if (value === "another") {
        setIsPastCycleDialogVisible(true);
        return;
      }
      setRelatedCycleId(value);

      // Preservation-only samples intentionally have no cycle to prefill.
      if (value === "preservation" || value === UNLINKED_SPECIMEN_CYCLE_ID) {
        return;
      }

      const availableCycles = [
        ...customRelatedCycles,
        ...(patientCycles?.length ? patientCycles : getDevRelatedCycles()),
      ] as RelatedCycleReference[];
      const selectedCycle = availableCycles.find((cycle) => cycle.id === value);
      if (!selectedCycle) return;

      const fertilisationDate = getRelatedCyclePhaseDate(
        selectedCycle,
        "fertilization",
        "end",
      );
      const freezeDate =
        getRelatedCyclePhaseDate(selectedCycle, "embryo_development", "end") ??
        (selectedCycle.cycleType === "embryo_freezing"
          ? (selectedCycle.endDate ?? selectedCycle.startDate ?? null)
          : null);

      setCycles((previous) =>
        previous.map((cycle, index) => {
          if (index !== 0) return cycle;
          const embryoUpdates: Partial<Cycle["embryo"]> = {};
          if (cycle.type === "embryos") {
            // Use the selected cycle as the default source of truth. Preserve
            // imported report values when they already exist so a partial
            // cycle record never silently overwrites a report.
            if (fertilisationDate && !cycle.embryo.fertilisationDate) {
              embryoUpdates.fertilisationDate = fertilisationDate;
            }
            if (freezeDate && !cycle.embryo.freezeDate) {
              embryoUpdates.freezeDate = freezeDate;
            }
          }
          return {
            ...cycle,
            ...(selectedCycle.clinicName || selectedCycle.provider
              ? {
                  clinicName:
                    selectedCycle.clinicName ?? selectedCycle.provider ?? "",
                }
              : {}),
            ...(normalizeCountryCode(selectedCycle.country)
              ? { country: normalizeCountryCode(selectedCycle.country)! }
              : {}),
            ...(Object.keys(embryoUpdates).length > 0
              ? { embryo: { ...cycle.embryo, ...embryoUpdates } }
              : {}),
          };
        }),
      );
    },
    [customRelatedCycles, patientCycles],
  );

  const handlePastCycleSave = useCallback((cycle: RelatedCycleReference) => {
    setCustomRelatedCycles((previous) => [cycle, ...previous]);
    setRelatedCycleId(cycle.id);
    setIsPastCycleDialogVisible(false);
  }, []);

  const relatedCycleOptions = useMemo<DropdownOption[]>(() => {
    const fallbackCycles = getDevRelatedCycles();
    const usingDevFallback =
      !patientCycles?.length && fallbackCycles.length > 0;
    const availableCycles = [
      ...customRelatedCycles,
      ...(patientCycles?.length ? patientCycles : fallbackCycles),
    ] as RelatedCycleReference[];
    const allowedTypes = manualType ? RELEVANT_CYCLE_TYPES[manualType] : [];
    const apiSpecimenType =
      manualType === "eggs"
        ? "oocyte"
        : manualType === "sperm"
          ? "semen"
          : "embryo";
    const specimenLabel = manualType
      ? t(`cryo_vault.add_modal.related_cycle_specimen.${manualType}`)
      : "specimens";
    const relevantCycles = availableCycles
      .filter((cycle) => allowedTypes.includes(cycle.cycleType))
      .sort((a, b) => {
        const aIsOpen = a.status === "open" ? 0 : 1;
        const bIsOpen = b.status === "open" ? 0 : 1;
        if (aIsOpen !== bIsOpen) return aIsOpen - bIsOpen;
        return (b.startDate ?? "").localeCompare(a.startDate ?? "");
      });

    const cycleOptions = relevantCycles.map((cycle, index) => {
      const typeLabelKey =
        CYCLE_TYPE_LABEL_KEYS[
          cycle.cycleType as keyof typeof CYCLE_TYPE_LABEL_KEYS
        ];
      const typeLabel = typeLabelKey ? t(typeLabelKey) : cycle.cycleType;
      const cycleNumber = cycle.cycleNumber ?? index + 1;
      const year = cycle.startDate?.slice(0, 4);
      const existingCount = usingDevFallback
        ? (cycle.existingSpecimenCount ?? 0)
        : (patientSpecimens ?? []).filter(
            (specimen) =>
              specimen.cycleId === cycle.id &&
              specimen.type === apiSpecimenType,
          ).length;

      return {
        id: cycle.id,
        label: `${typeLabel} #${cycleNumber}${year ? ` · ${year}` : ""}`,
        ...(existingCount > 0
          ? {
              detail: t("cryo_vault.add_modal.fields.related_cycle_existing", {
                params: {
                  count: String(existingCount),
                  specimen: specimenLabel,
                },
              }),
            }
          : {}),
      };
    });

    return [
      {
        id: UNLINKED_SPECIMEN_CYCLE_ID,
        label: t("cryo_vault.add_modal.fields.related_cycle_not_linked"),
      },
      ...cycleOptions,
      ...(manualType === "sperm"
        ? [
            {
              id: "preservation",
              label: t("cryo_vault.add_modal.fields.sperm_preservation"),
            },
          ]
        : []),
      {
        id: "another",
        label: t("cryo_vault.add_modal.fields.related_cycle_another"),
      },
    ];
  }, [customRelatedCycles, manualType, patientCycles, patientSpecimens, t]);

  const activeSource = cycles[0]?.source ?? manualSource;
  const offersRelatedCycle =
    activeSource === "mine" || activeSource === "partner";

  const formSteps = useMemo<FormStep[]>(() => {
    if (manualType === "sperm") {
      return [
        ...(offersRelatedCycle ? (["related"] as const) : []),
        "analysis",
        "counts",
        "details",
      ];
    }
    if (manualType === "embryos") {
      return [
        ...(offersRelatedCycle ? (["related"] as const) : []),
        "inventory",
        "development",
        "details",
      ];
    }
    if (manualType !== "eggs") return ["details"];

    return [
      ...(offersRelatedCycle ? (["related"] as const) : []),
      "counts",
      "usage",
      "details",
    ];
  }, [manualType, offersRelatedCycle]);

  /**
   * Her position, resolved from the step she is on rather than stored as a rank.
   *
   * A step that leaves the list — answering "donor" retires the related-cycle
   * step she may have been standing on — falls back to the first step rather
   * than to a neighbour chosen by arithmetic.
   */
  const formStepIndex = useMemo(() => {
    if (!formStepId) return 0;
    const found = formSteps.indexOf(formStepId);
    return found >= 0 ? found : 0;
  }, [formStepId, formSteps]);
  const currentFormStep = formSteps[formStepIndex] ?? "details";
  const isFirstFormStep = formStepIndex === 0;
  const isLastFormStep = formStepIndex >= formSteps.length - 1;

  // Validation
  const validCycles = useMemo(
    () =>
      cycles.filter((c) => {
        return isValidSpecimenCycle(c);
      }),
    [cycles],
  );

  const isValid = validCycles.length > 0;

  const hasFertilizedEmbryos = useMemo(
    () =>
      validCycles.some(
        (cycle) =>
          cycle.type === "eggs" && (cycle.oocyte.numberFertilized ?? 0) > 0,
      ),
    [validCycles],
  );

  // Pre-resolve translated error messages to avoid putting t in callback deps
  const validationErrorMessage = t(
    manualType === "embryos"
      ? "cryo_vault.add_modal.validation_error_embryos"
      : "cryo_vault.add_modal.validation_error",
  );
  const genericErrorMessage = t("cryo_vault.add_modal.generic_error");
  const batchSaveFailedMessage = t(
    "cryo_vault.add_modal.batch_save_failed_message",
  );
  const postSaveSyncErrorMessage = t(
    "cryo_vault.add_modal.post_save_sync_error",
  );
  const documentUploadErrorMessage = t(
    "cryo_vault.add_modal.document_upload_failed_message",
  );
  const retryPostSaveSyncLabel = t("cryo_vault.add_modal.retry_post_save_sync");
  const retryDocumentUploadLabel = t(
    "cryo_vault.add_modal.retry_document_upload",
  );
  const pendingDocumentNames = useMemo(
    () => getUnfiledSpecimenAttachmentNames(pendingDocumentCycles),
    [pendingDocumentCycles],
  );

  const uploadCycleDocuments = useCallback(
    async (inputCycles: Cycle[]) => {
      const nextCycles = inputCycles.map((cycle) => ({
        ...cycle,
        files: cycle.files.map((file) => ({ ...file })),
      }));
      const failedNames: string[] = [];

      for (const cycle of nextCycles) {
        const freezeDate =
          cycle.type === "eggs"
            ? cycle.oocyte.freezeDate
            : cycle.type === "sperm"
              ? cycle.sperm.freezeDate || cycle.sperm.collectedAt
              : cycle.embryo.freezeDate;
        const docDate = freezeDate || undefined;
        const specimenTags = (cycle.specimenIds ?? []).map(
          (specimenId) => `specimen:${specimenId}`,
        );

        for (const file of cycle.files) {
          if (!file.uploadId) {
            try {
              const source = await transfer.uploadSource(
                {
                  uri: file.uri,
                  name: file.name,
                  mimeType: file.mimeType,
                },
                { destination: "specimen" },
              );
              file.uploadId = source.uploadId;
            } catch {
              failedNames.push(file.name);
              continue;
            }
          }

          if (!file.uploadId || file.documentId) continue;
          try {
            file.documentId = await transfer.fileSource(
              {
                file: {
                  uri: file.uri,
                  name: file.name,
                  mimeType: file.mimeType,
                },
                uploadId: file.uploadId,
              },
              {
                name: `CryoVault: ${file.name}`,
                documentDate: docDate ?? null,
                tags: ["cryo-storage", ...specimenTags],
              },
            );
          } catch {
            failedNames.push(file.name);
          }
        }
      }

      return { cycles: nextCycles, failedNames };
    },
    [transfer],
  );

  const handleRetryPostSaveSync = useCallback(async () => {
    if (!pendingPostSavePrefill || !onSaved || saveState === "saving") return;

    setSaveState("saving");
    setErrorMessage("");
    try {
      await onSaved(pendingPostSavePrefill);
      setPendingPostSavePrefill(null);
      setSaveState("saved");
    } catch {
      setSaveState("error");
      setErrorMessage(postSaveSyncErrorMessage);
    }
  }, [onSaved, pendingPostSavePrefill, postSaveSyncErrorMessage, saveState]);

  const handleRetryDocumentUpload = useCallback(async () => {
    if (!pendingDocumentCycles || saveState === "saving") return;

    setSaveState("saving");
    setErrorMessage("");
    const result = await uploadCycleDocuments(pendingDocumentCycles);
    setCycles(result.cycles);
    if (result.failedNames.length > 0) {
      setPendingDocumentCycles(result.cycles);
      setSaveState("error");
      setErrorMessage(documentUploadErrorMessage);
      return;
    }

    setPendingDocumentCycles(null);
    const firstSavedCycle = result.cycles[0];
    const savedPrefill = firstSavedCycle
      ? buildSavedPrefill(firstSavedCycle)
      : null;
    if (savedPrefill && onSaved) {
      try {
        await onSaved(savedPrefill);
      } catch {
        setPendingPostSavePrefill(savedPrefill);
        setSaveState("error");
        setErrorMessage(postSaveSyncErrorMessage);
        return;
      }
    }
    setSaveState("saved");
  }, [
    documentUploadErrorMessage,
    onSaved,
    pendingDocumentCycles,
    postSaveSyncErrorMessage,
    saveState,
    uploadCycleDocuments,
  ]);

  // Save
  const handleSave = useCallback(async () => {
    if (!isValid) {
      setSaveState("error");
      setErrorMessage(validationErrorMessage);
      return;
    }
    setSaveState("saving");
    setErrorMessage("");

    let selectedCycleId: string | undefined;
    let specimenBatch: SpecimenBatchResult;
    try {
      const selectedRelatedCycleId =
        relatedCycleId === "preservation"
          ? UNLINKED_SPECIMEN_CYCLE_ID
          : relatedCycleId === "another"
            ? null
            : relatedCycleId;
      selectedCycleId = resolveSpecimenCycleId({
        relatedCycleId: selectedRelatedCycleId,
        contextCycleId: cycleId,
      });

      const selectedDraft = customRelatedCycles.find(
        (cycle) => cycle.id === relatedCycleId && cycle.isDraft,
      );
      if (selectedDraft) {
        const savedCycle = await saveCanonicalCycle({
          cycleType: selectedDraft.cycleType,
          status: "closed",
          startDate: selectedDraft.startDate,
          endDate: selectedDraft.endDate,
          clinic: selectedDraft.clinicName,
          provider: selectedDraft.provider,
        });
        selectedCycleId = savedCycle.cycle.id;
        setRelatedCycleId(savedCycle.cycle.id);
        setCustomRelatedCycles((previous) =>
          previous.map((cycle) =>
            cycle.id === selectedDraft.id
              ? {
                  ...cycle,
                  id: savedCycle.cycle.id,
                  isDraft: false,
                }
              : cycle,
          ),
        );
      }

      const firstCycle = validCycles[0];
      const selectedCycle = [
        ...customRelatedCycles,
        ...(patientCycles ?? []),
      ].find((cycle) => cycle.id === selectedCycleId);
      const canCreateCanonicalCycle =
        Boolean(firstCycle) &&
        (firstCycle?.type !== "sperm" || manualSource !== "donor");

      // OCR/manual intake can arrive without a preselected cycle. Establish
      // the durable cycle identity before writing specimen rows so a retry
      // reuses the same cycle instead of creating another one. Donor and
      // preservation-only semen remain specimen-only records by design.
      if (
        firstCycle &&
        canCreateCanonicalCycle &&
        shouldCreateSpecimenCycle({
          selectedCycleId,
          relatedCycleId: selectedRelatedCycleId,
        })
      ) {
        const savedCycle = await saveCanonicalCycle({
          cycleType: cycleTypeForSpecimenType(firstCycle.type),
          status: "closed",
          startDate: specimenCycleDate(firstCycle),
          endDate: specimenCycleDate(firstCycle),
          clinic: firstCycle.clinicName || null,
          provider: firstCycle.clinicName || null,
        });
        selectedCycleId = savedCycle.cycle.id;
        setRelatedCycleId(savedCycle.cycle.id);
      }

      const existingSampleIds = Object.fromEntries(
        validCycles
          .map((cycle, cycleIndex) => {
            if (cycle.type !== "sperm") return null;
            const sampleId = cycle.specimenIds
              ?.map(
                (specimenId) =>
                  patientSpecimens?.find(
                    (specimen) =>
                      specimen.type === "semen" && specimen.id === specimenId,
                  )?.sampleId,
              )
              .find((id): id is string => Boolean(id));
            return sampleId ? [String(cycleIndex), sampleId] : null;
          })
          .filter((entry): entry is [string, string] => entry !== null),
      );
      specimenBatch = await createSpecimen.mutateAsync({
        cycles: validCycles,
        cycleId: selectedCycleId,
        idempotencyKeys: pendingSpecimenIdempotencyKeys ?? undefined,
        sampleIds: pendingSpecimenSampleIds ?? existingSampleIds,
        operationKey: pendingSpecimenOperationKey ?? undefined,
      });

      /**
       * Backfill what the eggs taught us about the cycle they came from —
       * nothing more.
       *
       * This call used to send `status: "closed"` with `endDate === startDate`,
       * on a cycle the patient had selected rather than one being created. On a
       * live egg-freezing cycle that ended the treatment she was still in, and
       * `endDate === startDate` is how this codebase encodes a skipped cycle, so
       * it also recorded the treatment she had just been through as one that
       * never took place. The two nulls below it blanked her clinic whenever the
       * specimen form carried no clinic name of its own. All of that from saving
       * how many eggs were frozen.
       *
       * A cycle that does not exist yet is still created closed further up: a
       * specimen with no cycle really is a historical record. Here the cycle
       * exists, so its dates and its lifecycle belong to the flows that own them
       * — the treatment hub, and the end-cycle dialog she chooses to open.
       */
      if (firstCycle && selectedCycleId && firstCycle.type === "eggs") {
        const clinicName =
          firstCycle.clinicName || selectedCycle?.provider || null;
        await saveCanonicalCycle({
          cycleId: selectedCycleId,
          cycleType:
            selectedCycle?.cycleType ??
            cycleTypeForSpecimenType(firstCycle.type),
          ...(clinicName ? { clinic: clinicName, provider: clinicName } : {}),
        });
      }
    } catch (e) {
      if (e instanceof SpecimenBatchError) {
        setCycles(e.retryCycles);
        setPendingSpecimenIdempotencyKeys(e.retryIdempotencyKeys);
        setPendingSpecimenSampleIds(e.retrySampleIds);
        setPendingSpecimenOperationKey(e.operationKey);
        setSaveState("error");
        setErrorMessage(
          e.succeededCount > 0
            ? t("cryo_vault.add_modal.partial_save_message", {
                params: { count: String(e.failedCount) },
              })
            : batchSaveFailedMessage,
        );
        return;
      }
      setSaveState("error");
      setErrorMessage(e instanceof Error ? e.message : genericErrorMessage);
      return;
    }
    setPendingSpecimenIdempotencyKeys(null);
    setPendingSpecimenSampleIds(null);
    setPendingSpecimenOperationKey(null);

    try {
      const savedCycles = validCycles.map((cycle, cycleIndex) => ({
        ...cycle,
        specimenIds: [
          ...new Set([
            ...(cycle.specimenIds ?? []),
            ...(specimenBatch.specimenIdsByCycle[cycleIndex] ?? []),
          ]),
        ],
      }));
      const documentResult = await uploadCycleDocuments(savedCycles);
      setCycles(documentResult.cycles);
      if (documentResult.failedNames.length > 0) {
        setPendingDocumentCycles(documentResult.cycles);
        setSaveState("error");
        setErrorMessage(documentUploadErrorMessage);
        return;
      }

      setPendingDocumentCycles(null);
      const firstSavedCycle = documentResult.cycles[0];
      const savedPrefill = firstSavedCycle
        ? buildSavedPrefill(firstSavedCycle)
        : null;
      if (savedPrefill && onSaved) {
        try {
          await onSaved(savedPrefill);
        } catch {
          // The specimen is confirmed, but its dependent Treatment Hub sync
          // is not. Keep the form open with a retry for only that callback so
          // the patient never re-submits the specimen batch.
          setPendingPostSavePrefill(savedPrefill);
          setSaveState("error");
          setErrorMessage(postSaveSyncErrorMessage);
          return;
        }
      }
      setPendingPostSavePrefill(null);
      setSaveState("saved");
    } catch (e) {
      setSaveState("error");
      setErrorMessage(e instanceof Error ? e.message : genericErrorMessage);
    }
  }, [
    isValid,
    validCycles,
    cycleId,
    createSpecimen,
    patientCycles,
    patientSpecimens,
    documentUploadErrorMessage,
    relatedCycleId,
    customRelatedCycles,
    manualSource,
    saveCanonicalCycle,
    validationErrorMessage,
    genericErrorMessage,
    batchSaveFailedMessage,
    postSaveSyncErrorMessage,
    pendingSpecimenIdempotencyKeys,
    pendingSpecimenSampleIds,
    pendingSpecimenOperationKey,
    t,
    onSaved,
    uploadCycleDocuments,
  ]);

  const handleFormBack = useCallback(() => {
    if (isFirstFormStep) {
      setStep("upload");
      return;
    }
    setFormStepId(formSteps[Math.max(0, formStepIndex - 1)] ?? null);
  }, [formSteps, formStepIndex, isFirstFormStep]);

  const handleFormNext = useCallback(() => {
    if (isLastFormStep) {
      void handleSave();
      return;
    }
    setFormStepId(
      formSteps[Math.min(formSteps.length - 1, formStepIndex + 1)] ?? null,
    );
  }, [formSteps, formStepIndex, handleSave, isLastFormStep]);

  const formNextDisabled = false;

  const dialogMaxHeight = Math.max(360, Math.min(windowHeight - 32, 760));
  const formScrollMaxHeight = Math.max(240, dialogMaxHeight - 150);
  const formType = step === "form" ? (cycles[0]?.type ?? null) : null;
  const formStepLabel =
    formType === "embryos" && currentFormStep === "development"
      ? t(
          `cryo_vault.add_modal.steps.embryo_${cycles[0]?.embryoEntryMode === "individual" ? "shared_details" : "group_details"}`,
        )
      : t(`cryo_vault.add_modal.steps.${currentFormStep}`);
  const dialogTitle =
    formType === "sperm" && currentFormStep === "analysis"
      ? t("cryo_vault.add_modal.sperm.analysis_title")
      : formType
        ? t("cryo_vault.add_modal.form_title", {
            params: {
              type: t(`cryo_vault.add_modal.cycle_card.${formType}`),
            },
          })
        : t("cryo_vault.add_modal.header_title");
  const dialogIllustration = formType
    ? SPECIMEN_ILLUSTRATIONS[formType]
    : {
        source: CRYO_VAULT_ILLUSTRATION,
        width: 78,
        height: 78,
      };
  const dialogSubtitle =
    step === "upload" ? t("cryo_vault.add_modal.upload_intro") : undefined;

  return (
    <>
      <Dialog
        visible={visible}
        onClose={handleClose}
        layout="centered"
        closeButton="minimal"
        dismissOnBackdropTap={saveState !== "saving"}
        containerStyle={{
          width: "90%",
          maxWidth: 440,
          maxHeight: dialogMaxHeight,
        }}
        surfaceStyle={{ maxHeight: dialogMaxHeight }}
        contentStyle={{
          maxHeight: dialogMaxHeight,
          paddingTop: 24,
          paddingHorizontal: 0,
          paddingBottom: 0,
        }}
        testID="add-specimen-dialog"
      >
        {saveState === "saved" ? (
          <SpecimenAddedSuccess
            specimenType={formType ?? "eggs"}
            hasFertilizedEmbryos={hasFertilizedEmbryos}
            onAddEmbryos={handleAddResultingEmbryos}
            onAddAnotherSample={handleAddAnotherSample}
            onDismiss={handleClose}
          />
        ) : (
          <>
            <View style={{ alignItems: "center", paddingHorizontal: 24 }}>
              <DialogIcon
                source={dialogIllustration.source}
                width={dialogIllustration.width}
                height={dialogIllustration.height}
                style={{ marginBottom: 16 }}
              />
              <DialogHeader
                title={dialogTitle}
                subtitle={dialogSubtitle}
                style={{ alignItems: "center" }}
                titleStyle={{ textAlign: "center" }}
                subtitleStyle={{ textAlign: "center" }}
              />
            </View>

            {step === "upload" ? (
              /* ── UPLOAD STEP ── */
              <View
                style={{
                  paddingHorizontal: 20,
                  paddingTop: 22,
                  paddingBottom: 20,
                  gap: 20,
                }}
              >
                {isParsing ? (
                  <View
                    style={{
                      alignItems: "center",
                      paddingVertical: 32,
                      gap: 12,
                    }}
                  >
                    <ActivityIndicator size="large" color={CRYO_ACCENT} />
                    <Text style={{ fontSize: 13, color: TEXT_MID }}>
                      {t("cryo_vault.add_modal.extracting")}
                    </Text>
                  </View>
                ) : (
                  <>
                    {cycles[0]?.files[0] ? (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                          paddingHorizontal: 12,
                          paddingVertical: 11,
                          borderRadius: 12,
                          backgroundColor: `${SELECTION_ACCENT}08`,
                          borderWidth: 1,
                          borderColor: `${SELECTION_ACCENT}18`,
                        }}
                      >
                        <FileText size={17} color={SELECTION_ACCENT} />
                        <View style={{ flex: 1, gap: 2 }}>
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "600",
                              color: EMBER.colors.text,
                            }}
                            numberOfLines={1}
                          >
                            {cycles[0].files[0].name}
                          </Text>
                          <Text style={{ fontSize: 11, color: TEXT_MID }}>
                            {t("cryo_vault.add_modal.uploaded_file_hint")}
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <DocumentFileDropZone
                        onPick={() => void handlePickDocument()}
                        onFileSelected={(file) => void processUploadFile(file)}
                      />
                    )}
                    {failedUpload && (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                          padding: 12,
                          borderRadius: 12,
                          backgroundColor: `${WARM}10`,
                          borderWidth: 1,
                          borderColor: `${WARM}25`,
                        }}
                      >
                        <AlertTriangle size={16} color={WARM} />
                        <Text style={{ flex: 1, fontSize: 12, color: WARM }}>
                          {t("cryo_vault.add_modal.extraction_failed_message")}
                        </Text>
                        <SoftSecondaryButton
                          label={t("cryo_vault.add_modal.retry_extraction")}
                          onPress={() => {
                            void processUploadFile(failedUpload);
                          }}
                        />
                      </View>
                    )}
                    <View style={{ gap: 12 }}>
                      <Text
                        style={{
                          fontSize: 13,
                          fontWeight: "500",
                          color: TEXT_MID,
                          textAlign: "center",
                        }}
                      >
                        {t("cryo_vault.add_modal.manual_option")}
                      </Text>
                      <SpecimenTypePicker
                        value={manualType}
                        onChange={(type) => {
                          setManualType(type);
                          setManualSource(null);
                          setRelatedCycleId(null);
                          setFormStepId(null);
                          if (cycles[0]?.files.length) {
                            setCycles((prev) =>
                              prev.map((cycle, index) =>
                                index === 0 ? { ...cycle, type } : cycle,
                              ),
                            );
                          }
                        }}
                      />
                    </View>
                    {manualType && (
                      <View style={{ gap: 10 }}>
                        <Text
                          style={{
                            fontSize: 13,
                            fontWeight: "600",
                            color: EMBER.colors.text,
                            textAlign: "center",
                          }}
                        >
                          {t(
                            "cryo_vault.add_modal.source.who_specimen_from_optional",
                          )}
                        </Text>
                        <SpecimenSourcePicker
                          value={manualSource}
                          onChange={(source) => {
                            setManualSource(source);
                            setRelatedCycleId(null);
                          }}
                        />
                      </View>
                    )}
                    <DialogActions>
                      <SoftSecondaryButton
                        label={t("cryo_vault.add_modal.back")}
                        onPress={handleClose}
                        flex={1}
                      />
                      <DialogPrimaryButton
                        label={t("cryo_vault.add_modal.next")}
                        onPress={handleManualNext}
                        disabled={!manualType || !manualSource}
                        flex={1}
                      />
                    </DialogActions>
                  </>
                )}
              </View>
            ) : (
              /* ── FORM STEP ── */
              <>
                <ScrollView
                  key={`form-step-${currentFormStep}`}
                  style={{ maxHeight: formScrollMaxHeight }}
                  contentContainerStyle={{
                    paddingHorizontal: 20,
                    paddingTop: 16,
                    paddingBottom: 20,
                    gap: 20,
                  }}
                  showsVerticalScrollIndicator={false}
                >
                  {formSteps.length > 1 && (
                    <StepDots
                      current={formStepIndex}
                      total={formSteps.length}
                      label={formStepLabel}
                    />
                  )}
                  {cycles.map((cycle) => (
                    <CycleForm
                      key={cycle.id}
                      cycle={cycle}
                      formStep={currentFormStep}
                      source={activeSource}
                      relatedCycleOptions={relatedCycleOptions}
                      relatedCycleId={relatedCycleId}
                      onRelatedCycleChange={handleRelatedCycleChange}
                      onReportSelected={(file) =>
                        void processUploadFile(file, cycle.id)
                      }
                      onUpdate={(updates) =>
                        handleUpdateCycle(cycle.id, updates)
                      }
                    />
                  ))}
                </ScrollView>

                {/* Footer */}
                <View
                  style={{
                    paddingHorizontal: 20,
                    paddingBottom: 20,
                    paddingTop: 16,
                    borderTopWidth: 1,
                    borderColor: BORDER,
                  }}
                >
                  {/* Error */}
                  {saveState === "error" && errorMessage && (
                    <View
                      style={{
                        gap: 10,
                        padding: 10,
                        borderRadius: 10,
                        backgroundColor: `${WARM}10`,
                        borderWidth: 1,
                        borderColor: `${WARM}25`,
                      }}
                    >
                      <View style={{ flexDirection: "row", gap: 8 }}>
                        <AlertTriangle size={14} color={WARM} />
                        <Text style={{ fontSize: 12, color: WARM, flex: 1 }}>
                          {errorMessage}
                        </Text>
                      </View>
                      {pendingDocumentNames.length > 0 && (
                        <Text
                          style={{
                            color: TEXT_MID,
                            fontSize: 11,
                            lineHeight: 16,
                          }}
                        >
                          {t(
                            "cryo_vault.add_modal.document_upload_failed_files",
                            {
                              params: {
                                files: pendingDocumentNames.join(", "),
                              },
                            },
                          )}
                        </Text>
                      )}
                      {pendingPostSavePrefill && (
                        <DialogPrimaryButton
                          label={retryPostSaveSyncLabel}
                          onPress={() => void handleRetryPostSaveSync()}
                        />
                      )}
                      {pendingDocumentCycles && (
                        <DialogPrimaryButton
                          label={retryDocumentUploadLabel}
                          onPress={() => void handleRetryDocumentUpload()}
                        />
                      )}
                    </View>
                  )}

                  <DialogActions
                    style={{ marginTop: 0, alignItems: "stretch" }}
                  >
                    <IconButton
                      accessibilityLabel={t("cryo_vault.add_modal.back")}
                      onPress={handleFormBack}
                      disabled={saveState === "saving"}
                      size={48}
                      surface="soft"
                      backgroundColor="rgba(42,37,34,0.07)"
                      pressedBackgroundColor="rgba(42,37,34,0.11)"
                      pressedOpacity={1}
                      style={{ width: 48 }}
                    >
                      <ChevronLeft
                        size={20}
                        color={EMBER.colors.textLight}
                        strokeWidth={2}
                      />
                    </IconButton>
                    <IconButton
                      accessibilityLabel={t(
                        "cryo_vault.modal.add_files_button",
                      )}
                      onPress={() => void handlePickSupportingDocument()}
                      disabled={saveState === "saving"}
                      size={48}
                      surface="soft"
                      backgroundColor="rgba(42,37,34,0.07)"
                      pressedBackgroundColor="rgba(42,37,34,0.11)"
                      pressedOpacity={1}
                      style={{ width: 48 }}
                    >
                      <FolderPlus
                        size={18}
                        color={EMBER.colors.textLight}
                        strokeWidth={1.8}
                      />
                    </IconButton>
                    <DialogPrimaryButton
                      label={t(
                        isLastFormStep
                          ? "cryo_vault.add_modal.save"
                          : "cryo_vault.add_modal.next",
                      )}
                      onPress={handleFormNext}
                      disabled={
                        (isLastFormStep && !isValid) ||
                        formNextDisabled ||
                        saveState === "saving"
                      }
                      loading={saveState === "saving"}
                      flex={1}
                    />
                  </DialogActions>
                </View>
              </>
            )}
          </>
        )}
      </Dialog>
      <AddPastCycleDialog
        visible={isPastCycleDialogVisible}
        specimenType={manualType}
        onClose={() => setIsPastCycleDialogVisible(false)}
        onSave={handlePastCycleSave}
      />
    </>
  );
}
